// Lisbon, "Chestnut smoke over Alfama" (a mild, clear, slightly damp night in mid-November: São Martinho, the season of
// roast chestnuts). From the north end of the Miradouro de Santa Luzia, where it meets the Largo das Portas do Sol, we
// look east-north-east over Alfama (compressed across, never rearranged): the Graça hill on the left with its church
// tower, the twin white towers of São Vicente de Fora and the long monastery beside them under warmer, dimmer lights,
// the dark trees of the Campo de Santa Clara, then the floodlit dome of the Panteão Nacional (Santa Engrácia). To the
// right the terracotta roofs step down to the Tagus: a cruise ship lies lit at its terminal below the Panteão, Santo
// Estêvão's bell tower and São Miguel's onion spires poke out of the roofs, a ferry crosses the wide dark estuary to
// the far lights of the south bank, and a waning moon lays a glitter path on the water. In front: tram 28's line
// along the Largo, a yellow tram clanging past with its trolley pole sparking on the wire, a tasca with a fado
// guitarist and a singer in its window, laundry on a rack, cats on the roofs, gulls; on the terrace a chestnut
// roaster's cart puffs white smoke into a lamp's glow, an old man reads his paper on a stone bench, a couple leans on
// the azulejo-panelled parapet by the pergola, and yellow plane-tree leaves drift down at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  mix, makeCanvas, rect, makeHalo, drawHalo, text, FONT3, streak, reflect, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout (screen space) ================= */
// Real bearings from the terrace: São Vicente ~32°, the Panteão ~51°, the cruise terminal ~85°, Santo Estêvão ~94°,
// São Miguel ~144°. They keep that order left to right, squeezed together so the whole view fits.
const HZ = 122;                                       // the far shore of the Tagus, just under eye level
const PN = { cx: 232, x0: 201, x1: 263, roof: 99, tip: 39 };   // the Panteão: body, cornice row, the finial's tip
const SV = { l: 116, r: 162, tw: 11, top: 76, face: 98, base: 134 };   // São Vicente: the towers' left columns
const GR = { x: 34, top: 58 };                        // the Graça church's bell tower on the hill to the left
const BB = { x: 271, y: 125, w: 46, h: 20 };          // the painted ad on a gable wall (the site's billboard)
const BBW = { x0: 266, x1: 323, top: 118 };           // that gable wall (its foot hides behind the roofs)
const BB_LAMPS = [10, 36];                            // the ad's two gooseneck lamps (x offsets on the face)
const RIV = { x0: 262, x1: W, top: HZ + 1, bot: 182 };  // the river's water (the roofs hide its near part)
const SHIP = { x: 334, y: 141, w: 64, h: 27 };        // the cruise ship at its terminal (sprite box)
const MOON = { x: 436, y: 44, r: 6 };
const SE = { x: 408, top: 121 };                      // Santo Estêvão's bell tower (left column, tip row)
const SM = { x: 437, top: 152 };                      // São Miguel's twin onion spires
const WIRE = 147, RAIL = 193;                         // tram 28's contact wire and rail rows along the Largo
const ST_END = 190;                                   // the São Tomé houses across the Largo end here
const CH = { x0: 184, x1: 241, top: 139 };            // the corner house with the tasca (the tram passes behind it)
const TASCA = { x: 190, y: 169, w: 19, h: 17 };       // its window: the guitarist and the singer
const PAR = { x0: 170, top: 206, bot: 232 };          // the terrace's parapet, azulejo panels on its face
const LAMP = { x: 168, y: 157 };                      // the lantern on the parapet's end pillar
const CART = { x: 108, y: 236 };                      // the chestnut roaster's cart (left edge, ground row)
const CHIM = { x: 141, y: 181 };                      // the top of its smoke stack
const COL = { x0: 466, x1: 480 };                     // the pergola's column at the right edge

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** '#rrggbb' between two colours (for the pixel buffers; core's mix() gives an rgb() string for fillStyle) */
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b);
  return '#' + A.map((v, i) => Math.round(lerp(v, B[i], t)).toString(16).padStart(2, '0')).join('');
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/** a sprite from rows of palette keys ('.' = transparent) */
function sprite(rows, pal) {
  const p = pix(rows[0].length, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') p.set(i, j, pal[row[i]]); });
  return p.done();
}
/** FONT3 text into a pix buffer (sc = pixel scale) */
function textP(p, s, x, y, col, sc = 1) {
  let cx = x;
  for (const ch of s) { const g = FONT3[ch]; if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') p.fill(cx + i * sc, y + j * sc, sc, sc, col); }); cx += 4 * sc; }
}
/** flattened dithered ellipse of light (pools on the pavement), drawn additively */
function makePool(rx, ry, col, maxA) {
  const w = rx * 2 + 1, h = ry * 2 + 1, p = pix(w, h), c = hex(col);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const d = Math.hypot((x - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.5) * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, c, Math.round(q * maxA * 255));
  }
  return { c: p.done(), rx, ry };
}
function drawPool(pl, x, y, a) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
/** a flicker level: 1, with stutters inside the event windows */
function flick(t, ev, salt) {
  if (!inEvent(t, ev)) return 1;
  const hv = hash(salt, Math.floor(t * 20), 3);
  return hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.1;
}
/** a 1-px line into a pix buffer (build time) */
function lineP(p, x0, y0, x1, y1, col) {
  const n = Math.max(1, Math.ceil(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))));
  for (let k = 0; k <= n; k++) p.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
}
/** a lit window blinking on and off: on for fraction p of its n steps per loop */
const winOn = (w, t) => hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 4) < w.p;

/* ================= Sky: a clear, damp November night, the city's glow low down ================= */
const SKY = ['#050818', '#070b1f', '#0a0f26', '#0d132d', '#111834', '#161d3b', '#1b2242', '#222748', '#2a2c4e', '#332f52',
  '#3d3454', '#483a56', '#534056', '#5f4756'];
function renderSky() {
  const p = pix(W, HZ + 8);
  for (let y = 0; y < p.h; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / (HZ + 2)), 1.45) * 0.8;
    v += Math.max(0, 1 - Math.hypot((i - 190) / 290, (y - 128) / 58)) * 0.14;   // Lisbon's glow over the hills
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 64, (y - MOON.y) / 46)) * 0.13;   // the moon's damp halo
    p.set(i, y, pick(SKY, v + (vnoise(i / 26, y / 9, 5) - 0.5) * 0.05, i, y));
  }
  // the waning gibbous moon: a lit disc, its shadowed limb on the right, grey maria
  const { x: mx, y: my, r } = MOON;
  for (let y = -r; y <= r; y++) for (let x = -r; x <= r; x++) {
    const d = Math.hypot(x, y);
    if (d > r + 0.3) continue;
    const term = 0.5 * r * Math.sqrt(Math.max(0, 1 - (y / r) ** 2));      // the terminator ellipse
    if (x > term) { p.set(mx + x, my + y, pick(['#1c2040', '#262a4a'], 0.4 + (d > r - 0.8 ? 0.4 : 0), mx + x, my + y)); continue; }
    let v = 0.78 - (x / r) * 0.1 - (d > r - 0.7 ? 0.25 : 0);
    if (vnoise((x + 20) / 2.4, (y + 20) / 2.4, 9) > 0.62) v -= 0.3;         // maria
    if (x > term - 1.2) v -= 0.25;
    p.set(mx + x, my + y, pick(['#8a8468', '#b8b092', '#dcd4b4', '#f2ead0', '#fffae8'], v, mx + x, my + y));
  }
  return p.done();
}
let STARS = [];
function makeStars() {
  const r = rng(41), out = [];
  for (let k = 0; k < 1400 && out.length < 96; k++) {
    const x = Math.floor(r() * W), y = 1 + Math.floor(r() * 96);
    if (x < 122 && y < 48) continue;                                           // clear of the title overlay
    if (Math.hypot(x - MOON.x, y - MOON.y) < 26) continue;                     // washed out by the moon
    if (Math.abs(x - PN.cx) < 36 && y > PN.tip - 4) continue;                  // not inside the dome
    if (r() > 0.95 - y / 110) continue;                                        // thinning into the city glow
    const bright = r() < 0.07;
    out.push({ x, y, k: 20 + Math.floor(r() * 60), ph: r(), b: bright ? 0.85 : 0.18 + r() * 0.42, bright, col: r() < 0.3 ? '#ffe6c8' : r() < 0.5 ? '#d4e2ff' : '#f0eeff' });
  }
  return out;
}
function drawStars(t) {
  for (const s of STARS) {
    const a = s.b * (0.6 + 0.4 * wave(t, s.k, s.ph));
    ctx.fillStyle = s.col; ctx.globalAlpha = a; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.bright) { ctx.globalAlpha = a * 0.3; ctx.fillRect(s.x - 1, s.y, 1, 1); ctx.fillRect(s.x + 1, s.y, 1, 1); ctx.fillRect(s.x, s.y - 1, 1, 1); ctx.fillRect(s.x, s.y + 1, 1, 1); }
  }
  ctx.globalAlpha = 1;
}
/* thin stratus drifting in off the Atlantic, lit orange-grey from below by the city */
function renderClouds() {
  const h = 34, p = pix(W, h), r = rng(57), puffs = [];
  for (let k = 0; k < 7; k++) puffs.push({ x: r() * W, y: 6 + r() * 22, rx: 30 + r() * 50, ry: 1.2 + r() * 1.6 });
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = -9;
    for (const q of puffs) { let dx = x - q.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx / q.rx) ** 2 - ((y - q.y) / q.ry) ** 2); }
    v += 0.25 * Math.sin(TAU * 6 * x / W + y * 0.8) * Math.sin(TAU * 3 * x / W);
    if (v <= bayer(x, y) * 0.6) continue;
    p.set(x, y, v > 0.62 ? '#3a3350' : '#2a2946');
  }
  return p.done();
}
/** a jet on final approach to the airport, crossing over the river with its landing lights on */
const PLANE = { t0: 168, t1: 216, x0: 496, x1: -14, y0: 30, y1: 64 };
function drawPlane(t) {
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const s = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(PLANE.x0, PLANE.x1, s)), y = Math.round(lerp(PLANE.y0, PLANE.y1, s));
  rect(ctx, x - 3, y, 7, 1, '#2c2a48'); rect(ctx, x, y - 1, 2, 1, '#2c2a48'); rect(ctx, x + 3, y - 1, 1, 1, '#2c2a48');
  rect(ctx, x - 3, y, 1, 1, '#4aff8a'); rect(ctx, x + 2, y + 1, 1, 1, '#ff4a4a');
  rect(ctx, x - 4, y, 1, 1, '#fffcf0'); halo(HL.landing, x - 5, y, 0.8 + 0.2 * wave(t, 240, 0.1));   // the landing lights
  const f = frac(t * 0.9);
  if (f < 0.06 || (f > 0.16 && f < 0.21)) { rect(ctx, x + 1, y - 1, 1, 1, '#ffffff'); halo(HL.strobe, x + 1, y - 1, 0.9); }
}

/* ================= The south bank: Barreiro and Seixal's lights across the estuary, the Arrábida hills beyond ================= */
const shoreY = x => Math.round(HZ - 1 - 2 * smooth(330, 380, x) + smooth(392, 412, x) - 2 * smooth(440, 480, x) + (vnoise(x / 7, 0, 61) - 0.5) * 1.4);
const FAR_LIGHT = ['#ffb456', '#ffc670', '#ffd89a', '#f0e8ff', '#ffb456', '#d8e4ff'];
const CHIMNEYS = [{ x: 352, top: 103 }, { x: 358, top: 107 }];   // the old works' stacks, red lights on top
let FAR_TW = [];
function renderFar() {
  const p = pix(W, H), r = rng(62);
  FAR_TW = [];
  for (let x = 372; x < W; x++) {                                             // the Arrábida range, far off in the haze
    const top = Math.round(HZ - 4 - 5 * smooth(392, 468, x) + 1.4 * Math.sin(x * 0.08) + (vnoise(x / 5, 0, 64) - 0.5) * 1.5);
    for (let y = top; y < HZ; y++) p.set(x, y, y === top ? '#3a3654' : pick(['#2c2a48', '#322e4c'], 0.5, x, y));
  }
  for (let x = RIV.x0 - 8; x < W; x++) {
    const top = shoreY(x);
    for (let y = top; y <= HZ; y++) p.set(x, y, y === top ? '#2a2a44' : pick(['#0e1024', '#13152a', '#1a1b30'], 0.3 + (y - top) * 0.12, x, y));
  }
  for (const c of CHIMNEYS) for (let y = c.top; y < shoreY(c.x); y++) { p.set(c.x, y, '#1a1c32'); if (y > c.top + 3) p.set(c.x + 1, y, '#141628'); }
  for (let x = RIV.x0; x < W; x++) {                                          // a dense strip of lights along the shore
    if (r() > 0.62) continue;
    const top = shoreY(x), y = top + 1 + Math.floor(r() * Math.max(1, HZ - top)), col = FAR_LIGHT[Math.floor(r() * FAR_LIGHT.length)];
    if (r() < 0.22) FAR_TW.push({ x, y, col, k: 40 + Math.floor(r() * 90), ph: r() });
    else p.set(x, y, r() < 0.4 ? mixHex(col, '#1a1b30', 0.4) : col);
  }
  return trim(p);
}
function drawFar(t) {
  blit(FAR_C);
  for (const s of FAR_TW) { ctx.globalAlpha = 0.5 + 0.5 * wave(t, s.k, s.ph); rect(ctx, s.x, s.y, 1, 1, s.col); }
  ctx.globalAlpha = 1;
  const on = frac(t / 1.5) < 0.5;                                             // the stacks' aviation lights
  CHIMNEYS.forEach((c, i) => { if (on !== !!i) { rect(ctx, c.x, c.top - 1, 1, 1, '#ff4040'); halo(HL.red, c.x, c.top - 1, 0.6); } });
}

/* ================= The Tagus: wide and dark, the moon's glitter path, a ferry crossing ================= */
const WATER = ['#060918', '#090d1f', '#0c1226', '#10172d', '#151d35', '#1b243e', '#232c48'];
function renderWater() {
  const p = pix(W, H);
  for (let y = RIV.top; y < RIV.bot; y++) for (let x = RIV.x0; x < W; x++) {
    const d = (y - RIV.top) / (RIV.bot - RIV.top);
    let v = 0.5 - d * 0.34 + Math.max(0, 1 - Math.abs(x - MOON.x) / 70) * 0.14 * (1 - d);
    const rip = vnoise(x / 10, y * 1.7, 63);
    if (rip > 0.7) v += 0.12; else if (rip < 0.24) v -= 0.08;
    p.set(x, y, pick(WATER, v, x, y));
  }
  return trim(p);
}
// the ferry to the city's quays: out from behind the ship's terminal, across towards the right and a little nearer
const FERRY = { t0: 58, t1: 138, x0: 312, x1: 492, y0: 130, y1: 137 };
const FERRY_ROWS = ['........m.......', '....wwwwwww.....', '..wlwlwlwlwlwc..', '.oooooooooooooo.', 'dddddddddddddddd'];
function drawFerry(t) {
  if (t < FERRY.t0 || t >= FERRY.t1) return;
  const s = (t - FERRY.t0) / (FERRY.t1 - FERRY.t0), x = Math.round(lerp(FERRY.x0, FERRY.x1, s)), y = Math.round(lerp(FERRY.y0, FERRY.y1, s));
  const X = x - 8, Y = y - 5;
  for (let k = 1; k < 18; k++) {                                              // its wake, a pale line fading behind it
    if (hash(k, Math.floor(t * 4), 66) < 0.35) continue;
    ctx.globalAlpha = 0.4 * (1 - k / 18); rect(ctx, X - k, y + (k > 9 ? 1 : 0), 1, 1, '#8a96c8');
  }
  ctx.globalAlpha = 0.35; ctx.drawImage(FERRY_REF, X, y + 1); ctx.globalAlpha = 1;   // its lit windows in the water
  ctx.drawImage(FERRY_C, X, Y);
  if (frac(t / 2) < 0.5) rect(ctx, X + 8, Y, 1, 1, '#ffffff');
  halo(HL.ferry, x, y - 2, 0.7);
}
/* the damp: a thin mist lying on the far water, drifting */
function renderMist() {
  const w = W, h = 16, p = pix(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const a = TAU * x / w;                                                    // periodic across w, so the two copies tile
    const v = Math.sin(Math.PI * y / h) * (0.3 + 0.35 * (1 + Math.sin(3 * a + 0.6 * Math.sin(7 * a) + y * 0.2)) / 2
      + 0.35 * (1 + Math.sin(5 * a + 1.3 + 0.4 * Math.sin(11 * a))) / 2);
    const q = Math.floor(v * 4 + bayer(x, y)) / 4;
    if (q > 0) p.set(x, y, '#9a9ab8', Math.round(q * 0.3 * 255));
  }
  return p.done();
}

/* ================= The hill: Graça, São Vicente de Fora and its monastery, the Panteão ================= */
const hillTop = x => {
  const y = x < 120 ? lerp(84, 100, smooth(0, 120, x)) : x < 210 ? lerp(100, 107, (x - 120) / 90) : lerp(107, 127, smooth(210, 272, x));
  return Math.round(y + 1.5 * Math.sin(x * 0.11) + (vnoise(x / 4, 0, 71) - 0.5) * 2);
};
const HILLR = ['#0b0d1b', '#101223', '#16182a', '#1c1e31', '#242638'];
const STONE = ['#1a1a2e', '#26263c', '#34344a', '#46445a', '#5a566a', '#706a7c', '#88808e', '#a096a0', '#b8acb0', '#cec0bc',
  '#e0d4ca', '#eee4d6', '#faf2e4'];
const WALLS = ['#3a3040', '#40384a', '#4a3a3a', '#3e3a30', '#34384a', '#443236'];
const HOUSE_LIGHT = ['#ffc070', '#ffd49a', '#ffb45a', '#e8e0ff', '#ffc070'];
let HILL_WIN = [];
// São Vicente's stone under its own floodlights: warmer and dimmer than the Panteão's, so the hero stands out
const STONE_SV = ['#1c1a2a', '#28243a', '#363046', '#463e50', '#58505c', '#6c6268', '#827472', '#98887c', '#ac9a88', '#bcaa94',
  '#c8b49c', '#d2bea4', '#dcc8ac'];
const stone = (p, x, y, lv, s = 33, ramp = STONE) => p.set(x, y, pick(ramp, lv + (hash(x, y, s) - 0.5) * 0.05, x, y));
function paintHillHouses(p) {
  const r = rng(81), houses = [];
  for (let k = 0; k < 170; k++) {
    const x = Math.floor(r() * (RIV.x0 + 4)) - 4, w = 5 + Math.floor(r() * 8), h = 4 + Math.floor(r() * 5);
    houses.push({ x, w, h, top: hillTop(x + (w >> 1)) + 2 + Math.floor(r() * 34), wall: WALLS[Math.floor(r() * WALLS.length)], r: r() });
  }
  houses.sort((a, b) => a.top - b.top);                                        // up the slope first
  for (const hs of houses) {
    for (let y = hs.top; y < hs.top + hs.h + 2; y++) for (let x = hs.x; x < hs.x + hs.w; x++) {
      if (y < hillTop(x) + 1 || y > 151) continue;
      const roof = y < hs.top + 2;
      p.set(x, y, roof ? (y === hs.top ? '#4e2c26' : '#3a2020') : x === hs.x + hs.w - 1 ? mixHex(hs.wall, '#0a0a14', 0.4) : hs.wall);
    }
    for (let y = hs.top + 3; y < hs.top + hs.h + 1; y += 3) for (let x = hs.x + 1; x < hs.x + hs.w - 1; x += 2) {
      if (y > 150 || y < hillTop(x) + 2) continue;
      const v = r();
      if (v < 0.3) p.set(x, y, HOUSE_LIGHT[Math.floor(r() * HOUSE_LIGHT.length)]);
      else if (v < 0.4) HILL_WIN.push({ id: 300 + HILL_WIN.length, x, y, w: 1, h: 1, col: '#ffc878', n: 2 + Math.floor(r() * 5), off: r(), p: 0.55 });
      else p.set(x, y, '#141624');
    }
  }
}
function paintGraca(p) {
  const x0 = GR.x - 4;                                                        // the tower's left column
  for (let y = 76; y < 94; y++) for (let x = 14; x < 31; x++) {               // the nave's gable end
    if (y < 76 + Math.abs(x - 22) * 0.6) continue;
    stone(p, x, y, 0.36 + (x < 22 ? 0.06 : -0.04) + (y === Math.ceil(76 + Math.abs(x - 22) * 0.6) ? 0.14 : 0));
  }
  for (let y = 78; y < 96; y++) for (let x = 39; x < 74; x++) {               // the convent's long block
    let lv = y < 80 ? 0.2 : 0.34 + (hash(x >> 2, 1, 35) - 0.5) * 0.06;
    if (y >= 80 && (y - 83) % 5 === 0 && (x - 40) % 3 === 0) lv = 0.14;
    stone(p, x, y, lv);
  }
  for (let y = GR.top; y < 96; y++) for (let x = x0; x < x0 + 9; x++) {        // the bell tower, floodlit faintly
    const e = x - x0, dx = e - 4;
    if (y < 65) { const hw = [0, 0, 1, 1, 2, 3, 3][y - GR.top]; if (Math.abs(dx) > hw) continue; }   // the cross, then the cap
    let lv = 0.46 + (e < 2 ? 0.08 : e > 6 ? -0.1 : 0) + smooth(66, 96, y) * 0.1;
    if (y === 65) lv += 0.16; else if (y === 66) lv -= 0.12;
    if (y >= 68 && y <= 73 && (e === 2 || e === 6)) lv = 0.1;                   // belfry openings
    if (y === GR.top || y === GR.top + 1) lv = 0.6;
    stone(p, x, y, lv);
  }
  for (const x of [48, 56, 64]) { p.set(x, 88, '#ffc878'); p.set(x, 83, '#ffd8a0'); }
}
const WING_X1 = 189;                                  // the monastery wing ends here; Santa Clara's trees fill the gap to the Panteão
function paintSaoVicente(p) {
  const fx0 = SV.l + SV.tw, fx1 = SV.r - 1, fc = (fx0 + fx1) / 2, half = (fx1 - fx0) / 2, S = (x, y, lv) => stone(p, x, y, lv, 33, STONE_SV);
  // the monastery's long south wing to the right of the church, towards the Panteão
  for (let y = 102; y < SV.base + 4; y++) for (let x = SV.r + SV.tw; x < WING_X1; x++) {
    let lv = 0.36 + smooth(104, 134, y) * 0.08 - (x - SV.r) * 0.002;
    if (y < 105) { p.set(x, y, y === 102 ? '#5a3228' : '#3e2220'); continue; }   // its tiled roof
    if (y === 105) lv += 0.12;
    if (x === WING_X1 - 1) lv -= 0.16;                                         // its end wall, turned from the light
    const wy = (y - 108) % 7, wx = (x - (SV.r + SV.tw) - 1) % 4;
    if (y >= 108 && wy < 3 && wx < 2) {
      if (hash(x >> 2, (y - 108) / 7 | 0, 36) < 0.2) { p.set(x, y, wy === 0 ? '#ffd49a' : '#f0a856'); continue; }
      lv = 0.14;
    }
    S(x, y, lv);
  }
  // the façade between the towers: a low pediment, pilasters, the upper windows, niches, the galilee's three arches
  for (let y = SV.face - 5; y < SV.base + 4; y++) for (let x = fx0; x <= fx1; x++) {
    const ped = SV.face - Math.round(5 * (1 - Math.abs(x - fc) / half));
    if (y < ped) continue;
    const bay = Math.floor((x - fx0) / 7), e = (x - fx0) % 7;
    let lv = 0.64 + smooth(SV.face, SV.base, y) * 0.14 - Math.abs(x - fc) / 90;
    if (y === ped) lv += 0.16; else if (y === ped + 1 && y < SV.face) lv -= 0.1;
    if (y === SV.face) lv += 0.18; else if (y === SV.face + 1) lv -= 0.2;
    if (y === 117) lv += 0.14; else if (y === 118) lv -= 0.18;                 // the entablature between the orders
    if (e === 0 || x === fx1) lv += 0.1;
    if (y >= 104 && y <= 111 && e >= 3 && e <= 4) {                            // upper windows (bays 1-3), niches with saints (0, 4)
      if (bay === 0 || bay === 4) lv = y === 104 ? 0.3 : y > 107 && y < 111 ? 0.58 : 0.2;
      else lv = y === 104 ? 0.34 : 0.12;
    }
    if (y >= 122 && bay >= 1 && bay <= 3 && e >= 2 && e <= 5 && !(y === 122 && (e === 2 || e === 5))) {   // the galilee's arches
      p.set(x, y, y < 126 ? '#6a4630' : pick(['#8a5a34', '#b87c44', '#d8a060'], 0.2 + (y - 124) / 12, x, y)); continue;
    }
    S(x, y, lv);
  }
  // the twin towers: belfries, a balustrade, bell-shaped cupolas and finials
  for (const tx of [SV.l, SV.r]) {
    const right = tx > fc, cxT = tx + 5;
    for (let y = SV.top - 4; y < SV.base + 4; y++) for (let x = tx; x < tx + SV.tw; x++) {
      const e = x - tx, dx = x - cxT;
      if (y < SV.top) { if (dx !== 0) continue; p.set(x, y, y === SV.top - 3 ? '#e8d8b0' : '#8a8070'); continue; }   // finial with a ball
      if (y < 82) {                                                            // the cupola
        const hw = [1, 2, 3, 3, 4, 4][y - SV.top];
        if (Math.abs(dx) > hw) continue;
        S(x, y, 0.5 - dx * 0.06 + (y === SV.top ? 0.1 : 0) + (right ? -0.06 : 0)); continue;
      }
      if (y < 84) { if (y === 82 && e % 2 === 1) continue; S(x, y, 0.62); continue; }   // the balustrade
      let lv = 0.62 + smooth(86, 134, y) * 0.12 + (right ? -0.08 : 0);
      if (e === 0 || e === SV.tw - 1) lv -= 0.14; else if (e === 1) lv += 0.08;
      if (y === 84) lv += 0.16; else if (y === 85) lv -= 0.18;
      if (y === SV.face) lv += 0.16; else if (y === SV.face + 1) lv -= 0.18;
      if (y === 117) lv += 0.12; else if (y === 118) lv -= 0.16;
      if (y >= 88 && y <= 95 && e >= 3 && e <= 7 && !(y === 88 && (e === 3 || e === 7))) {   // the belfry's arch, a bell in it
        p.set(x, y, y >= 90 && y <= 92 && e >= 4 && e <= 6 ? (y === 90 ? '#a8864a' : '#6a5230') : '#1c1a2a'); continue;
      }
      if (y >= 104 && y <= 108 && e >= 4 && e <= 6) lv = 0.16;
      if (y >= 123 && y <= 128 && e >= 4 && e <= 6) lv = 0.18;
      S(x, y, lv);
    }
  }
}
/* the trees of the Campo de Santa Clara in the gap between the monastery and the Panteão, dark against the sky, the
   Panteão's floodlight catching their right flanks */
const SANTA_CLARA = [[185, 113, 4, 4], [191, 107, 5, 5], [197, 103, 6, 6], [201, 110, 4, 5], [194, 113, 5, 4]];
function paintSantaClara(p) {
  for (let y = 95; y < 120; y++) for (let x = 180; x < 206; x++) {
    let best = -9, sx = 0, sy = 0;
    for (const [cx, cy, rx, ry] of SANTA_CLARA) {
      const dx = (x - cx) / rx, dy = (y - cy) / ry, v = 1 - dx * dx - dy * dy;
      if (v > best) { best = v; sx = dx; sy = dy; }
    }
    const edge = best + (hash(x, y, 37) - 0.5) * 0.35 + (vnoise(x / 2, y / 2, 36) - 0.5) * 0.3;
    if (edge <= 0) continue;
    const v = 0.3 - sy * 0.25 + (vnoise(x / 1.5, y / 1.5, 38) - 0.5) * 0.4 + (edge < 0.25 && sy < -0.4 ? 0.3 : 0);   // moonlit crowns
    p.set(x, y, sx > 0.35 && hash(x, y, 39) < 0.7 ? pick(['#20262a', '#343630', '#4a4438'], v + 0.2, x, y) : pick(['#0a1014', '#0f171a', '#152022', '#1e2c2e'], v, x, y));
  }
}
function paintPanteao(p) {
  const { cx, x0, x1 } = PN, S = (x, y, lv) => stone(p, x, y, lv, 34);
  for (let y = 52; y <= 74; y++) {                                             // the dome, ribbed, floodlit from the square
    const s = (74 - y) / 22.6, hwf = 15.5 * Math.sqrt(Math.max(0, 1 - s * s)), hw = Math.round(hwf);
    for (let dx = -hw; dx <= hw; dx++) {
      const n = dx / (hwf + 0.5);
      let lv = 0.36 + 0.42 * Math.sqrt(Math.max(0, 1 - n * n)) * (1 - 0.3 * s) - n * 0.1 + (1 - s) * 0.06;
      for (const nk of [-0.71, 0, 0.71]) {
        const d = dx - nk * hwf;
        if (Math.abs(d) < 0.55) lv += 0.14; else if (d >= 0.55 && d < 1.5) lv -= 0.07;
      }
      if (y >= 68 && y <= 69 && [-0.4, 0.36].some(nk => Math.abs(dx - nk * hwf) < 0.8)) lv = 0.2;   // small lucarnes
      if (Math.abs(dx) === hw) lv -= 0.12;
      S(cx + dx, y, lv);
    }
  }
  for (let y = 75; y <= 98; y++) {                                             // the drum: paired pilasters, tall windows
    const hw = y >= 93 ? 16 : 14;
    for (let dx = -hw; dx <= hw; dx++) {
      const n = dx / (hw + 0.5), m = (dx + 60) % 5;
      let lv = 0.44 + 0.32 * Math.sqrt(1 - n * n) - n * 0.08 + smooth(75, 98, y) * 0.06;
      if (y < 92) {
        if (m === 0) lv += 0.12;
        else if (m >= 2 && m <= 3 && y >= 79 && y <= 89 && Math.abs(n) < 0.92) lv = y === 79 ? 0.3 : 0.14;
      }
      if (y === 75) lv += 0.22; else if (y === 76) lv -= 0.16;
      if (y === 92) lv += 0.14; else if (y === 93) lv -= 0.1;
      S(cx + dx, y, lv);
    }
  }
  for (let y = 43; y <= 51; y++) {                                             // the lantern and its cap
    const hw = y < 46 ? [1, 2, 3][y - 43] : 4;
    for (let dx = -hw; dx <= hw; dx++) {
      if (y >= 47 && y <= 50 && Math.abs(dx) % 2 === 1 && Math.abs(dx) < 4) { p.set(cx + dx, y, y === 47 ? '#ffe8b8' : '#ffc878'); continue; }
      S(cx + dx, y, 0.5 + (y === 46 ? 0.2 : 0) - dx * 0.05);
    }
  }
  p.set(cx, 42, '#e8d8b0'); p.set(cx, 41, '#b0a080'); p.set(cx, 40, '#b0a080'); p.set(cx - 1, 40, '#8a8070'); p.set(cx + 1, 40, '#8a8070'); p.set(cx, 39, '#c8b890');
  for (let y = 95; y < 152; y++) for (let x = x0 + 11; x < x1 - 10; x++) {    // the undulating main front between the towers
    const dx = x - cx, u = dx / 21;
    if (y < 99) {                                                               // the balustrade round the roof terrace
      if (y === 95 || y === 98) S(x, y, y === 95 ? 0.64 : 0.46);
      else if ((x & 1) === 0) S(x, y, 0.54);
      continue;
    }
    let lv = 0.54 + 0.17 * Math.cos(u * Math.PI / 2) + smooth(100, 150, y) * 0.14 - (dx > 0 ? 0.05 : 0);
    if (y === 99) lv += 0.2; else if (y === 100) lv -= 0.2;
    if (y === 121) lv += 0.14; else if (y === 122) lv -= 0.14;
    if (Math.abs(dx) % 7 === 4) lv += 0.1; else if (Math.abs(dx) % 7 === 5) lv -= 0.06;   // pilasters
    if (y >= 105 && y <= 116 && Math.abs(dx) <= 1 && !(y === 105 && dx !== 0)) lv = 0.14;   // the great window over the door
    if (y >= 107 && y <= 116 && Math.abs(Math.abs(dx) - 15) <= 1 && !(y === 107 && Math.abs(dx) !== 15)) lv = y > 110 && Math.abs(dx) === 15 ? 0.5 : 0.18;   // niches with statues
    const arch = [-10, 0, 10].find(a => Math.abs(dx - a) <= 2);
    if (arch !== undefined && y >= 127 && !(y === 127 && Math.abs(dx - arch) === 2)) {   // the portico's three arches
      p.set(x, y, y < 131 ? '#4a3430' : pick(['#6a4834', '#94643e', '#b8844a'], 0.3 + (y - 131) / 16, x, y)); continue;
    }
    S(x, y, lv);
  }
  for (const tx of [x0, x1 - 10]) {                                           // the corner towers, never finished
    const right = tx > cx;
    for (let y = 85; y < 152; y++) for (let x = tx; x <= tx + 10; x++) {
      const e = x - tx;
      if (y < 88 && !(e === 2 || e === 3 || e === 7 || e === 8)) continue;     // two pinnacle stubs
      if (y >= 88 && y < 90 && (e < 1 || e > 9)) continue;                      // a stepped block
      let lv = 0.6 + smooth(92, 146, y) * 0.16 - (right ? 0.1 : 0);
      if (e === 0 || e === 10) lv -= 0.16; else if (e === 1 || e === 9) lv += 0.08;
      if (y === 90) lv += 0.2; else if (y === 91) lv -= 0.18;
      if (y === 99) lv += 0.16; else if (y === 100) lv -= 0.14;
      if (y >= 104 && y <= 117 && e >= 4 && e <= 6 && !(y === 104 && e !== 5)) lv = 0.15;
      if (y >= 94 && y <= 97 && e === 5) lv = 0.18;
      if (y >= 124 && y <= 130 && e >= 4 && e <= 6) lv = 0.2;
      S(x, y, lv);
    }
  }
}
function renderHill() {
  const p = pix(W, H);
  HILL_WIN = [];
  for (let x = 0; x < RIV.x0 + 6; x++) {
    const top = hillTop(x);
    for (let y = top; y < 152; y++) {
      let v = 0.3 + (vnoise(x / 3, y / 2.5, 82) - 0.5) * 0.3 - smooth(0, 30, y - top) * 0.08;
      if (y === top) v += 0.34;
      p.set(x, y, pick(HILLR, v, x, y));
    }
  }
  paintHillHouses(p); paintGraca(p); paintSaoVicente(p); paintSantaClara(p); paintPanteao(p);
  return trim(p);
}
/* the São Vicente pigeons: they burst off the towers at the bell, wheel round and settle again */
const BELLS = [118, 121.6, 125.2];
const PIGEONS = Array.from({ length: 9 }, (_, i) => ({ i, tower: i % 2, ph: i / 9, rx: 22 + (i * 7) % 13, ry: 7 + (i * 3) % 5, dir: i % 3 ? 1 : -1 }));
function drawPigeons(t) {
  const s = (t - BELLS[0]) / 30;
  if (s < 0 || s >= 1) return;
  const out = smooth(0, 0.12, s) * (1 - smooth(0.78, 1, s));
  for (const pg of PIGEONS) {
    const px0 = (pg.tower ? SV.r : SV.l) + 5, py0 = 83, a = TAU * (pg.dir * s * 1.6 + pg.ph);
    const ox = 144 + Math.cos(a) * pg.rx * 1.4, oy = 66 + Math.sin(a) * pg.ry;
    const x = Math.round(lerp(px0, ox, out)), y = Math.round(lerp(py0, oy, out));
    const up = frac(t * 5 + pg.ph) < 0.5;
    ctx.fillStyle = '#b0a8b4';
    ctx.fillRect(x, y, 1, 1);
    if (out > 0.05) { ctx.fillRect(x - 1, y - (up ? 1 : 0), 1, 1); ctx.fillRect(x + 1, y - (up ? 1 : 0), 1, 1); }
  }
}
function drawHillFx(t) {
  for (const w of HILL_WIN) if (winOn(w, t)) rect(ctx, w.x, w.y, w.w, w.h, w.col);
  halo(HL.lantern, PN.cx, 48, 0.8 + 0.1 * wave(t, 17, 0.3));                  // the Panteão's lit lantern
  halo(HL.flood, PN.cx, 118, 0.55); halo(HL.floodSV, 144, 120, 0.32);          // floodlight spill on the fronts, São Vicente's warmer
  drawPigeons(t);
}

/* ================= The billboard: a painted ad on a riverside gable wall, for warm custard tarts ================= */
// A made-up pastry shop's ad: a pastel de nata on azulejo blue, "NATA / QUENTINHA" (nice and warm). Without the
// billboard the tall gable stays, bare, with its few small windows.
const WALLR = ['#141220', '#1c1826', '#26202e', '#322a36', '#40363e', '#504448', '#625450', '#766658', '#8c7a64'];
const wallTop = x => BBW.top + Math.round(Math.abs(x - (BBW.x0 + BBW.x1) / 2) * 0.14);   // a shallow gable
function renderGableWall(board) {
  const p = pix(W, H);
  for (let x = BBW.x0; x < BBW.x1; x++) for (let y = wallTop(x); y < 180; y++) {
    let v = 0.22 + (vnoise(x / 6, y / 6, 131) - 0.5) * 0.14 + (vnoise(x / 2, y / 2, 132) - 0.5) * 0.06 + smooth(150, 180, y) * 0.06;
    if (board) v += 0.42 * Math.max(0, 1 - Math.hypot((x - BB.x - BB.w / 2) / 40, (y - BB.y - 6) / 24));
    if (x >= BBW.x1 - 3) v -= 0.12;                                            // the corner turning away
    if (y < wallTop(x) + 2) v = y === wallTop(x) ? 0.55 : 0.1;                 // coping and its shadow
    if (y > 150 && (y - 150) % 9 === 0) v -= 0.08;                            // old floor lines under the plaster
    p.set(x, y, pick(WALLR, v, x, y));
  }
  if (!board) [[277, 128, 0], [296, 129, 1], [287, 138, 0], [307, 139, 0], [278, 147, 1]].forEach(([vx, vy, lit]) => {   // the flats' side windows
    p.fill(vx, vy, 3, 4, lit ? '#e8a454' : '#0e0c16'); if (lit) p.fill(vx, vy, 3, 1, '#ffd08a'); p.fill(vx - 1, vy + 4, 5, 1, '#4a3e44');
  });
  if (board) {
    for (let x = BB.x - 1; x <= BB.x + BB.w; x++) { p.set(x, BB.y - 1, '#2a2230'); p.set(x, BB.y + BB.h, '#1a1620'); }
    for (let y = BB.y - 1; y <= BB.y + BB.h; y++) { p.set(BB.x - 1, y, '#2a2230'); p.set(BB.x + BB.w, y, '#1a1620'); }
    for (const lx of BB_LAMPS) {                                              // gooseneck lamps reaching out over the ad
      const X = BB.x + lx;
      p.fill(X, BB.y - 5, 1, 4, '#1c1a24'); p.fill(X - 3, BB.y - 6, 4, 1, '#24222c'); p.fill(X - 4, BB.y - 5, 1, 1, '#24222c');
      p.fill(X - 5, BB.y - 4, 3, 1, '#34303a');
    }
  }
  return trim(p);
}
const AD_BG = ['#101c5a', '#14226a', '#18297a', '#1e318a'];
const TART_ROWS = ['...ccccc...', '.cyyyByyyc.', 'cyByyyyyByc', 'cyyyyByyyyc', '.cyyyyyyyc.', 'kfkfkfkfkfk', '.kfkfkfkfk.', '..kfkfkfk..', '...kkkkk...'];
const TART = { x: 2, y: 4 };
const BIG_N = ['11..11', '11..11', '111.11', '111.11', '111.11', '11.111', '11.111', '11.111', '11..11', '11..11'];
function renderAd() {
  const p = pix(BB.w, BB.h), m = pix(BB.w, BB.h);
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < BB.w; i++) {
    let v = 0.3 + BB_LAMPS.reduce((s, lx) => s + 0.36 * Math.max(0, 1 - Math.hypot((i - lx) / 20, j / 22)), 0);
    if (i % 5 === 0 || j % 5 === 0) v -= 0.3;                                  // the tile joints of the painted azulejo
    p.set(i, j, pick(AD_BG, v, i, j));
    if (i === 0 || j === 0 || i === BB.w - 1 || j === BB.h - 1) p.set(i, j, '#dfe6f6');   // a white frame
    else if (i === 1 || j === 1 || i === BB.w - 2 || j === BB.h - 2) p.set(i, j, '#3d6bff');
  }
  const pal = { c: '#c8782a', y: '#f4c648', B: '#6a3010', f: '#e8a04a', k: '#8a4a1c' };
  TART_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') { p.set(TART.x + i, TART.y + j, pal[row[i]]); m.set(TART.x + i, TART.y + j, '#ffffff'); } });
  const txt = (s, x, y, sc, col, sh) => { if (sh) textP(p, s, x + 1, y + 1, sh, sc); textP(p, s, x, y, col, sc); textP(m, s, x, y, '#ffffff', sc); };
  // FONT3's N has no diagonal and reads as a "Π" at twice the size, so the big N is drawn by hand in the same 6×10 box
  const bigN = (x, y, col, sh) => {
    const on = (i, j) => BIG_N[j][i] === '1';
    for (let j = 0; j < 10; j++) for (let i = 0; i < 6; i++) if (on(i, j)) p.set(x + i + 1, y + j + 1, sh);
    for (let j = 0; j < 10; j++) for (let i = 0; i < 6; i++) if (on(i, j)) { p.set(x + i, y + j, col); m.set(x + i, y + j, '#ffffff'); }
  };
  bigN(14, 2, '#fff4dc', '#0a1040');
  txt('ATA', 22, 2, 2, '#fff4dc', '#0a1040');
  txt('QUENTINHA', 5, 13, 1, '#ffc94a', '#0a1040');
  return { c: p.done(), mask: m.done() };
}
const BB_FLICK = [[81.3, 82.4], [199.2, 200.1]];     // the right lamp stutters
function drawBillboard(t, pitch) {
  const hum = 0.95 + 0.05 * wave(t, 1500, 0.2), off = inEvent(t, BB_FLICK) && hash(77, Math.floor(t * 18), 2) < 0.5;
  halo(HL.bbGlow, BB.x + BB.w / 2, BB.y + 6, (off ? 0.7 : 1) * hum);
  if (!BB_PITCH.covers(t, pitch)) {
    ctx.drawImage(AD.c, BB.x, BB.y);
    for (let k = 0; k < 3; k++) {                                             // steam curling up off the tart
      const u = frac(t * 86 / LOOP + k / 3), x = BB.x + TART.x + 3 + k * 2 + Math.round(Math.sin(u * 5 + k) * 1.1), y = BB.y + TART.y - Math.round(u * 4);
      ctx.globalAlpha = 0.6 * Math.sin(Math.PI * u); rect(ctx, x, y, 1, 1, '#f4ecff');
    }
    ctx.globalAlpha = 1;
    const sp = step(t, 160);                                                  // cinnamon and caramel glinting
    if (hash(sp, 3, 142) < 0.5) { const q = Math.floor(hash(sp, 4, 142) * 4); rect(ctx, BB.x + TART.x + [3, 5, 7, 4][q], BB.y + TART.y + [1, 2, 1, 3][q], 1, 1, '#fff8e0'); }
    const f = frac(t / 16);                                                   // a gloss sheen rolls over the paint
    if (f < 0.1) {
      const bx = lerp(-10, BB.w + 10, f / 0.1);
      for (let j = 0; j < BB.h; j++) {
        const sx = Math.round(bx - j * 0.6), a = Math.max(0, sx), b = Math.min(BB.w, sx + 3);
        if (b <= a) continue;
        ctx.globalAlpha = 0.1; rect(ctx, BB.x + a, BB.y + j, b - a, 1, '#ffffff');
        ctx.globalAlpha = 0.5; ctx.drawImage(AD.mask, a, j, b - a, 1, BB.x + a, BB.y + j, b - a, 1);
      }
      ctx.globalAlpha = 1;
    }
    if (off) { ctx.globalAlpha = 0.35; rect(ctx, BB.x + 24, BB.y, 22, BB.h, '#060416'); ctx.globalAlpha = 1; }
  }
  BB_PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, k) => {
    if (off && k === 1) return;
    rect(ctx, BB.x + lx - 5, BB.y - 4, 3, 1, '#fff4dc');
    halo(HL.bbLamp, BB.x + lx - 4, BB.y - 3, hum);
  });
}

/* ================= The cruise ship lying at its terminal below the Panteão ================= */
let SHIP_WIN = [];
function renderShip() {
  const { w, h } = SHIP, p = pix(w, h), r = rng(151);
  SHIP_WIN = [];
  const hullL = y => Math.round(Math.max(0, (y - 19) * 0.4));                // the raked bow on the left
  for (let y = 19; y < h; y++) for (let x = hullL(y); x < w; x++) {
    let v = 0.62 - (y - 19) * 0.04 + (x > w - 3 ? -0.2 : 0);
    if (y >= h - 2) { p.set(x, y, '#23243a'); continue; }
    if (y === 19) v += 0.18;
    p.set(x, y, pick(['#3a3c52', '#5a5c72', '#8a8ca2', '#b4b6c8', '#d4d6e2'], v, x, y));
  }
  for (const [y0, y1, x0, x1] of [[16, 19, 2, 63], [12, 16, 4, 61], [8, 12, 7, 58], [6, 8, 11, 54]]) {   // decks stepping up
    for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) p.set(x, y, y === y0 ? '#e2e4ee' : x === x1 - 1 ? '#6a6c84' : '#b8bccc');
    if (y1 - y0 < 4) continue;
    for (let x = x0 + 1; x < x1 - 1; x += 2) {                                 // rows of cabin and balcony lights
      const y = y0 + 2, v = r();
      if (v < 0.62) p.set(x, y, v < 0.1 ? '#dce8ff' : v < 0.4 ? '#fff0d0' : '#ffd8a0');
      else if (v < 0.72) SHIP_WIN.push({ id: 500 + SHIP_WIN.length, x: SHIP.x + x, y: SHIP.y + y, w: 1, h: 1, col: '#ffe8c0', n: 2 + Math.floor(r() * 4), off: r(), p: 0.6 });
      else p.set(x, y, '#2a2e48');
      p.set(x, y + 1, '#8a8ea4');
    }
  }
  for (let x = 8; x < 57; x += 8) { p.fill(x, 17, 5, 2, '#e07a2c'); p.fill(x, 17, 5, 1, '#f8a050'); }   // lifeboats
  p.fill(7, 9, 10, 2, '#1a2040'); for (let x = 8; x < 16; x += 2) p.set(x, 9, '#4a6aa8');               // the bridge's windows
  for (let x = 4; x < 62; x += 3) if (r() < 0.4) p.set(x, 21 + (x % 2) * 2, '#ffe0a8');                  // portholes
  p.fill(44, 1, 7, 5, '#1c2448'); p.fill(44, 1, 7, 1, '#3a4478'); p.fill(44, 3, 7, 1, '#c8ccd8');          // the funnel
  for (let y = 0; y < 6; y++) p.set(15, y, '#6a6c84');                                                   // the mast
  for (let k = 0; k <= 16; k++) {                                                                        // dressing lights
    const c = k % 2 ? '#ffe8b0' : '#6a6c84';
    p.set(lerp(15, 1, k / 16), lerp(0, 15, k / 16), c); p.set(lerp(15, 44, k / 16), lerp(0, 1, k / 16), c); p.set(lerp(50, 63, k / 16), lerp(1, 15, k / 16), c);
  }
  return p.done();
}
const SHIP_HORN = 100;
function drawShip(t) {
  halo(HL.ship, SHIP.x + 32, SHIP.y + 14, 0.55);
  ctx.drawImage(SHIP_C, SHIP.x, SHIP.y);
  for (const w of SHIP_WIN) if (winOn(w, t)) rect(ctx, w.x, w.y, 1, 1, w.col);
  if (frac(t / 1.2) < 0.35) { rect(ctx, SHIP.x + 15, SHIP.y - 1, 1, 1, '#ffffff'); halo(HL.strobe, SHIP.x + 15, SHIP.y - 1, 0.6); }
  rect(ctx, SHIP.x + 47, SHIP.y, 1, 1, frac(t / 2) < 0.5 ? '#ff4a4a' : '#8a2020');
  for (let k = 0; k < 7; k++) {                                              // the funnel's plume when she sounds her horn
    const u = (t - SHIP_HORN - k * 0.35) / 4.5;
    if (u < 0 || u >= 1) continue;
    ctx.globalAlpha = 0.5 * (1 - u); rect(ctx, SHIP.x + 46 + Math.round(u * 10 + k % 2), SHIP.y - Math.round(u * 12), 2 + Math.round(u * 3), 2, '#dcdcec');
  }
  ctx.globalAlpha = 1;
}

/* ================= Alfama: terracotta roofs stepping down to the river, lanes glowing between them ================= */
const TILE = ['#241212', '#34181a', '#48201e', '#5e2c24', '#76382a', '#8e4630', '#a85838', '#c06c44'];
const FACADES = [
  ['#2a2430', '#3e3440', '#564a50', '#6e6060', '#8a7a74'], ['#2e2218', '#4a3522', '#664a2c', '#80603a', '#9c7a48'],
  ['#2e1a20', '#4a2830', '#663a40', '#804c50', '#9a6260'], ['#161e30', '#22304c', '#304466', '#425a80', '#56729a'],
  ['#1c2820', '#2a3c2e', '#3c523e', '#50684e', '#688060'], ['#30281a', '#4a3e24', '#665630', '#80703c', '#9a884a'],
];
const ROWS = [
  { y: 166, h: [3, 6], w: [5, 9], rh: 2, win: 1 }, { y: 172, h: [4, 7], w: [6, 11], rh: 2, win: 1 },
  { y: 180, h: [5, 9], w: [8, 13], rh: 3, win: 2 }, { y: 189, h: [6, 10], w: [9, 15], rh: 3, win: 2 },
  { y: 199, h: [7, 12], w: [11, 18], rh: 4, win: 2 }, { y: 210, h: [8, 13], w: [13, 21], rh: 5, win: 3 },
];
let ROOF_WIN = [], ALLEYS = [], LINES = [], CAT_RIDGE = null, TVS = [];
const glowAt = (x, y) => ALLEYS.reduce((s, a) => s + a.k * Math.max(0, 1 - Math.hypot((x - a.x) / a.r, (y - a.y) / (a.r * 0.7))) ** 1.6, 0);
function paintHouse(p, hs, r) {
  const { x0, x1, b, ft, rt, type, fac, win } = hs;
  for (let x = x0; x < x1; x++) for (let y = ft; y < b + 4; y++) {             // the façade
    if (x < 0 || x >= W) continue;
    let v = 0.3 + glowAt(x, y) * 0.9 + (vnoise(x / 3, y / 3, 160 + hs.id) - 0.5) * 0.1;
    if (y <= ft + 1) v -= 0.14;                                               // under the eaves
    if (x === x1 - 1) v -= 0.1;
    p.set(x, y, pick(fac, v, x, y));
  }
  const ww = win, wh = win + 1, sx = win + 2;                                 // windows in a grid, lit here and there
  for (let y = ft + 2; y + wh <= b + 2; y += wh + 2) for (let x = x0 + 1 + ((x1 - x0 - 1) % sx >> 1); x + ww <= x1 - 1; x += sx) {
    const v = r();
    if (v < 0.24) { p.fill(x, y, ww, wh, v < 0.03 ? '#9ab8ff' : v < 0.14 ? '#ffc070' : '#ffdca0'); if (wh > 2) p.fill(x, y + wh - 1, ww, 1, '#e89040'); }
    else if (v < 0.36) ROOF_WIN.push({ id: 700 + ROOF_WIN.length, x, y, w: ww, h: wh, col: r() < 0.2 ? '#ffe8c8' : '#ffc278', n: 2 + Math.floor(r() * 6), off: r(), p: 0.6 });
    else { p.fill(x, y, ww, wh, '#0e1220'); if (win > 1 && r() < 0.4) { p.fill(x - 1, y, 1, wh, '#243a2e'); p.fill(x + ww, y, 1, wh, '#243a2e'); } }   // dark glass, green shutters
    if (win > 1) p.fill(x, y + wh, ww, 1, pick(fac, 0.8, x, y));             // the sill
  }
  if (type === 'flat') {                                                      // a roof terrace: a lit parapet edge, sometimes a mansard
    for (let x = x0; x < x1; x++) { p.set(x, ft - 1, pick(fac, 0.75 + glowAt(x, ft) * 0.5, x, ft - 1)); p.set(x, ft, pick(fac, 0.2, x, ft)); }
    if (x1 - x0 > 9 && r() < 0.5) { const mx = x0 + 2 + Math.floor(r() * (x1 - x0 - 8)); p.fill(mx, ft - 4, 5, 3, pick(fac, 0.5, mx, ft)); p.fill(mx + 1, ft - 3, 2, 2, '#ffcc80'); p.fill(mx - 1, ft - 5, 7, 1, TILE[4]); }
    return;
  }
  for (let y = rt; y < ft; y++) {                                             // the tiled roof, hipped or plain
    const k = y - rt, ins = type === 'hip' ? Math.round((ft - 1 - y) * 1.1) : 0, over = y === ft - 1 ? 1 : 0;
    for (let x = x0 - over + ins; x < x1 + over - ins; x++) {
      if (x < 0 || x >= W) continue;
      let v = 0.36 + glowAt(x, y + 3) * 0.7 + (x - x0) / (x1 - x0) * 0.1;   // the moon from the right
      if (k === 0) v += 0.2;                                                  // the ridge
      if (k % 2 === 1) v -= 0.1; else if ((x + (k >> 1)) % 2 === 0) v += 0.05;   // courses of tiles
      if (y === ft - 1) v -= 0.08;
      p.set(x, y, pick(TILE, v + (hash(x, y, 171) - 0.5) * 0.08, x, y));
    }
  }
  if (r() < 0.35) { const cx = x0 + 2 + Math.floor(r() * Math.max(1, x1 - x0 - 5)), ch = 2 + Math.floor(r() * 2); p.fill(cx, rt - ch, 2, ch + 1, '#b4aca4'); p.fill(cx - 1, rt - ch - 1, 4, 1, '#7a6e68'); }   // a white chimney
  if (r() < 0.14) { const ax = x0 + 1 + Math.floor(r() * (x1 - x0 - 2)); for (let y = rt - 6; y < rt; y++) p.set(ax, y, '#2a2830'); p.fill(ax - 2, rt - 5, 5, 1, '#2a2830'); }   // an old aerial
}
function paintChurches(p, which) {
  const S = (x, y, lv) => stone(p, x, y, lv, 181);
  if (which === 0) {                                                          // Santo Estêvão: its bell tower and dome
    const x0 = SE.x;
    for (let y = SE.top - 2; y < 186; y++) for (let x = x0; x < x0 + 8; x++) {
      const e = x - x0, dx = x - (x0 + 3.5);
      if (y < SE.top + 6) { const hw = [0.5, 0.5, 0.5, 1.5, 2.5, 3.5, 4, 4][y - SE.top + 2]; if (Math.abs(dx) > hw) continue; }
      let lv = 0.56 + smooth(128, 186, y) * 0.14 - (e > 5 ? 0.1 : 0) + (e < 2 ? 0.06 : 0);
      if (y === SE.top + 6) lv += 0.18; else if (y === SE.top + 7) lv -= 0.16;
      if (y >= SE.top + 9 && y <= SE.top + 15 && (e === 2 || e === 5) && !(y === SE.top + 9)) lv = 0.12;   // belfry arches
      if (y === SE.top + 18) lv += 0.14; else if (y === SE.top + 19) lv -= 0.1;
      if (y >= SE.top + 23 && y <= SE.top + 26 && e >= 3 && e <= 4) lv = 0.18;
      S(x, y, lv);
    }
    for (let y = 150; y < 186; y++) for (let x = x0 + 8; x < x0 + 20; x++) {  // the church beside it, a low dome over the nave
      const dx = x - (x0 + 14), top = y < 157 ? 150 + Math.round(dx * dx * 0.2) : 157;
      if (y < top) continue;
      let lv = y < 157 ? 0.5 - dx * 0.04 + (y === top ? 0.16 : 0) : 0.42 + smooth(157, 186, y) * 0.12;
      if (y === 157) lv += 0.16; else if (y === 158) lv -= 0.14;
      if (y >= 161 && y <= 166 && (x - x0) % 4 === 2) lv = 0.16;
      S(x, y, lv);
    }
    return;
  }
  for (const tx of [SM.x, SM.x + 12]) {                                       // São Miguel's twin towers with onion spires
    for (let y = SM.top - 3; y < 196; y++) for (let x = tx; x < tx + 7; x++) {
      const dx = x - (tx + 3), e = x - tx;
      if (y < SM.top) { if (dx !== 0) continue; p.set(x, y, y === SM.top - 2 ? '#e0d0a0' : '#8a806c'); continue; }
      if (y < SM.top + 7) {                                                   // the onion
        const hw = [1, 2, 3, 3, 2, 1, 2][y - SM.top];
        if (Math.abs(dx) > hw) continue;
        S(x, y, 0.5 - dx * 0.09 + (y === SM.top + 2 ? 0.1 : 0)); continue;
      }
      let lv = 0.52 + smooth(160, 196, y) * 0.12 - (e > 4 ? 0.1 : 0);
      if (y === SM.top + 7) lv += 0.18; else if (y === SM.top + 8) lv -= 0.12;
      if (y >= SM.top + 10 && y <= SM.top + 14 && e >= 2 && e <= 4 && !(y === SM.top + 10 && e !== 3)) lv = 0.12;
      S(x, y, lv);
    }
  }
  for (let y = SM.top + 16; y < 196; y++) for (let x = SM.x + 7; x < SM.x + 12; x++) {   // the front between them, a pediment
    const lv = 0.48 + (y === SM.top + 16 ? 0.16 : 0) + (y > SM.top + 22 && y < SM.top + 28 && x === SM.x + 9 ? -0.3 : 0);
    S(x, y, lv);
  }
}
function renderRoofs() {
  const p = pix(W, H), r = rng(161);
  ROOF_WIN = []; LINES = []; TVS = []; CAT_RIDGE = null;
  ALLEYS = [];
  for (let k = 0; k < 18; k++) ALLEYS.push({ x: 238 + r() * 240, y: 160 + r() * 48, r: 10 + r() * 12, k: 0.3 + r() * 0.3 });
  ALLEYS.push({ x: 262, y: 186, r: 20, k: 0.4 }, { x: 372, y: 196, r: 22, k: 0.45 });
  let id = 0;
  ROWS.forEach((row, ri) => {
    if (ri === 2) paintChurches(p, 0);
    if (ri === 3) paintChurches(p, 1);
    let x = 232 - Math.floor(r() * 8);
    while (x < W + 2) {
      let w = row.w[0] + Math.floor(r() * (row.w[1] - row.w[0] + 1));
      const b = row.y + Math.floor(r() * 5) - 2, fh = row.h[0] + Math.floor(r() * (row.h[1] - row.h[0] + 1)) + (r() < 0.15 ? 3 : 0);
      let type = r() < 0.52 ? 'hip' : r() < 0.55 ? 'gable' : 'flat';
      if (ri === 3 && x > 292 && x < 312 && !CAT_RIDGE) { w = 50; type = 'gable'; }   // a long ridge the cat walks along
      const ft = b - fh, rt = ft - (type === 'flat' ? 0 : row.rh);
      const hs = { id: id++, x0: x, x1: x + w, b, ft, rt, type, fac: FACADES[Math.floor(r() * FACADES.length)], win: row.win };
      paintHouse(p, hs, r);
      if (ri === 3 && w === 50 && type === 'gable' && !CAT_RIDGE) CAT_RIDGE = { x0: x + 3, x1: x + w - 4, y: rt };
      if (ri >= 3 && r() < 0.3 && !LINES.some(l => Math.abs(l.x0 - x) < 40)) LINES.push({ x0: x + w - 1, x1: x + w + 12 + Math.floor(r() * 6), y: ft + 3 + Math.floor(r() * 3), seed: id });
      x += w + (r() < 0.3 ? 2 + Math.floor(r() * 4) : 0);                      // sometimes a lane between
    }
  });
  const pickTv = ROOF_WIN.filter(w => w.w >= 2);
  for (let k = 0; k < 3 && pickTv.length; k++) TVS.push(pickTv[Math.floor(r() * pickTv.length)]);
  return trim(p);
}
const ALLEY_FLICK = [[57.1, 58.3], [171.6, 172.4]];
function drawRoofFx(t) {
  for (const w of ROOF_WIN) {
    if (TVS.includes(w)) {                                                    // a television's blue flicker
      const f = hash(w.id, Math.floor(t * 6), 172);
      rect(ctx, w.x, w.y, w.w, w.h, f < 0.3 ? '#5a7ad8' : f < 0.7 ? '#8aa6f0' : '#c8d8ff'); continue;
    }
    if (winOn(w, t)) rect(ctx, w.x, w.y, w.w, w.h, w.col);
  }
  ALLEYS.forEach((a, i) => halo(HL.alley, a.x, a.y, (0.55 + a.k * 0.6) * (i === 4 ? flick(t, ALLEY_FLICK, 21) : 1)));
  for (const l of LINES) {                                                    // laundry strung across the lanes
    ctx.fillStyle = '#1a1620';
    for (let x = l.x0; x <= l.x1; x++) ctx.fillRect(x, l.y + Math.round(Math.sin(Math.PI * (x - l.x0) / (l.x1 - l.x0)) * 1.4), 1, 1);
    for (let k = 0; k < 3; k++) {
      const u = (k + 1) / 4, x = Math.round(lerp(l.x0, l.x1, u)), y = l.y + Math.round(Math.sin(Math.PI * u) * 1.4) + 1;
      const col = ['#d8d0c4', '#b8404a', '#4a6aa8', '#d8b84a'][(l.seed + k) % 4], sw = wave(t, 31 + k * 7, l.seed * 0.1 + k * 0.3) > 0.5 ? 1 : 0;
      rect(ctx, x + sw, y, 2, 3, col);
    }
  }
}

/* ================= The Largo: the houses across the tram line, the contact wire ================= */
const AZ4 = ['2112', '1001', '1001', '2112'];                            // a four-tile azulejo motif (0 white .. 2 cobalt)
const AZ_COOL = [['#4a5270', '#6e7898', '#98a2c0', '#c0c8e0'], ['#24345e', '#34488a', '#4a62b0', '#6480d0'], ['#141c40', '#1c2a66', '#2a3c94', '#3d58c8']];
const AZ_WARM = [['#6a5a50', '#968270', '#c4ac92', '#eadcc4'], ['#3a3a5e', '#52528a', '#6c6eb0', '#8a8ed0'], ['#1e1e44', '#2a2e6a', '#3a4298', '#5060c4']];
/** an azulejo façade pixel: the motif's colour, lit by v (0..1), warmer as warm (0..1) rises */
function azulejo(x, y, v, warm, sc = 1) {
  const m = +AZ4[Math.floor(y / sc) & 3][Math.floor(x / sc) & 3], ramp = warm > 0.5 ? AZ_WARM[m] : AZ_COOL[m];
  if (Math.floor(x / sc) % 4 === 0 && Math.floor(y / sc) % 4 === 0) v -= 0.12;   // the joints
  return pick(ramp, v, x, y);
}
const ST = [
  { x0: -4, x1: 34, top: 137, fac: 1, shop: 'cafe' }, { x0: 34, x1: 70, top: 134, fac: -1, shop: 'ginja' },
  { x0: 70, x1: 112, top: 138, fac: 2 }, { x0: 112, x1: 150, top: 135, fac: 0, shop: 'grocery' },
  { x0: 150, x1: ST_END + 4, top: 137, fac: 5 },
];
const ST_LAMPS = [{ x: 52, y: 158 }, { x: 131, y: 158 }];                // wall lanterns on brackets
const HANGERS = [20, 80, 140];                                           // where span wires hold up the contact wire
let ST_WIN = [];
const stGlow = (x, y) => ST_LAMPS.reduce((s, l) => s + 0.5 * Math.max(0, 1 - Math.hypot((x - l.x) / 26, (y - l.y) / 22)) ** 1.4, 0);
function renderStreet() {
  const p = pix(W, H), r = rng(191);
  ST_WIN = [];
  for (const hs of ST) {
    const az = hs.fac < 0, fac = FACADES[Math.max(0, hs.fac)];
    for (let x = Math.max(0, hs.x0); x < hs.x1; x++) {
      for (let y = hs.top - 4; y < hs.top; y++) {                             // the roof's front slope, seen from a little above
        let v = 0.34 + (y - hs.top + 4) * 0.06 + stGlow(x, y + 6) * 0.3;
        if (y === hs.top - 4) v += 0.16; if ((y - hs.top) % 2 === 0) v -= 0.08;
        p.set(x, y, pick(TILE, v + (hash(x, y, 192) - 0.5) * 0.06, x, y));
      }
      for (let y = hs.top; y < RAIL; y++) {
        const g = stGlow(x, y), v = 0.22 + g * 0.8 + (vnoise(x / 3, y / 3, 193 + hs.x0) - 0.5) * 0.08 - (y < hs.top + 2 ? 0.12 : 0) - (x === hs.x1 - 1 ? 0.1 : 0);
        p.set(x, y, az ? azulejo(x, y, v, g) : pick(fac, v, x, y));
      }
    }
    for (const wy of [hs.top + 5, hs.top + 19]) for (let wx = hs.x0 + 5; wx + 5 < hs.x1 - 2; wx += 11) {   // two floors of sash windows
      p.fill(wx - 1, wy - 1, 7, 10, '#8a8078'); p.fill(wx - 1, wy + 9, 7, 1, '#b8ae9e');
      const v = r(), lit = v < 0.4, tog = !lit && v < 0.6;
      p.fill(wx, wy, 5, 9, lit ? '#e8a050' : '#10131f');
      if (lit) { p.fill(wx, wy, 5, 2, '#ffd89a'); p.fill(wx + 2, wy, 1, 9, '#b86a2c'); }
      else { p.fill(wx + 2, wy, 1, 9, '#2a2c3a'); if (r() < 0.5) { p.fill(wx - 2, wy, 1, 9, '#27493a'); p.fill(wx + 5, wy, 1, 9, '#27493a'); } }
      if (tog) ST_WIN.push({ id: 900 + ST_WIN.length, x: wx, y: wy, w: 5, h: 9, col: '#eaa452', n: 2 + Math.floor(r() * 5), off: r(), p: 0.55 });
      if (r() < 0.3) { p.fill(wx - 1, wy + 10, 7, 1, '#1c1a20'); for (let k = 0; k < 7; k += 2) p.set(wx - 1 + k, wy + 11, '#1c1a20'); }   // an iron balcony
    }
    const d0 = hs.x0 + ((hs.x1 - hs.x0) >> 1) - 6;                            // the ground floor
    if (hs.shop === 'cafe') { p.fill(d0 - 2, 172, 18, 21, '#2a1a14'); p.fill(d0, 174, 14, 19, '#d8904a'); p.fill(d0, 174, 14, 3, '#ffd49a'); p.fill(d0 + 6, 176, 1, 17, '#7a4a28'); textP(p, 'CAFE', d0 - 1, 166, '#fff0d8'); }
    else if (hs.shop === 'ginja') { p.fill(d0, 175, 9, 18, '#c8742e'); p.fill(d0, 175, 9, 2, '#ffc070'); p.fill(hs.x0 + 1, 165, 34, 8, '#6a5a48'); p.fill(hs.x0 + 2, 166, 32, 6, '#3a0e16'); textP(p, 'GINJINHA', hs.x0 + 3, 166, '#ffd8a0'); p.fill(d0 + 12, 178, 7, 7, '#10131f'); p.fill(d0 + 12, 178, 7, 1, '#8a8078'); }
    else if (hs.shop === 'grocery') { p.fill(hs.x0 + 3, 174, 30, 19, '#e8b060'); p.fill(hs.x0 + 3, 174, 30, 2, '#fff0c8'); for (let k = 0; k < 28; k += 3) p.fill(hs.x0 + 4 + k, 187, 2, 2, k % 2 ? '#f08a2a' : '#e8c040'); p.fill(hs.x0 + 3, 184, 30, 1, '#6a4a2a'); p.fill(hs.x0 - 1, 165, 38, 8, '#6a5a48'); p.fill(hs.x0, 166, 36, 6, '#e8dcc0'); textP(p, 'MERCEARIA', hs.x0 + 1, 166, '#2a1a10'); }
    else { p.fill(d0 + 1, 176, 9, 17, '#3a2418'); p.fill(d0 + 1, 176, 9, 1, '#6a4a30'); p.set(d0 + 8, 185, '#c8a060'); }
  }
  for (let x = 0; x < ST_END + 6; x++) {                                      // the Largo's setts and tram rails
    for (let y = RAIL; y < 200; y++) {
      const g = stGlow(x, y - 20) * 0.6, v = 0.24 + g + (hash(x >> 1, y, 194) - 0.5) * 0.16 + (y > 197 ? 0.04 : 0);
      p.set(x, y, pick(['#0e0e16', '#16151e', '#1e1c26', '#2a2630', '#38323a'], v, x, y));
    }
    p.set(x, 195, (x & 3) ? '#5a5660' : '#7a7680'); p.set(x, 198, (x & 3) ? '#6a6670' : '#8a8690');
  }
  for (let x = 0; x < ST_END + 2; x++) p.set(x, WIRE, (x & 7) ? '#3e3a48' : '#4e4a58');                // the contact wire
  for (const hx of HANGERS) { lineP(p, hx - 3, WIRE - 5, hx, WIRE - 1, '#34303c'); lineP(p, hx + 3, WIRE - 5, hx, WIRE - 1, '#34303c'); p.set(hx - 3, WIRE - 6, '#4a4450'); p.set(hx + 3, WIRE - 6, '#4a4450'); }
  for (const l of ST_LAMPS) { p.fill(l.x - 4, l.y - 6, 5, 1, '#1a1820'); p.set(l.x - 4, l.y - 5, '#1a1820'); }   // lantern brackets
  return trim(p);
}
function drawStreetFx(t) {
  for (const w of ST_WIN) if (winOn(w, t)) { rect(ctx, w.x, w.y, w.w, w.h, w.col); rect(ctx, w.x, w.y, w.w, 2, '#ffd89a'); }
  for (const l of ST_LAMPS) {
    rect(ctx, l.x - 1, l.y - 5, 3, 1, '#141218'); rect(ctx, l.x - 1, l.y - 4, 3, 4, '#ffe2a4'); rect(ctx, l.x, l.y - 3, 1, 2, '#fff6dc'); rect(ctx, l.x - 1, l.y, 3, 1, '#141218');
    halo(HL.wall, l.x, l.y - 2, 0.9);
  }
  halo(HL.shop, 18, 184, 0.7); halo(HL.shop, 130, 183, 0.75); halo(HL.shopS, 50, 184, 0.7);
}

/* ================= Tram 28: a yellow "remodelado" clanging along the Largo, its pole sparking on the wire ================= */
const TRAM_W = 62, TRAM_H = 34, TRAM_Y = RAIL + 5 - (TRAM_H - 1);             // the sprite's top row on screen: its wheels stand on the near rail
function renderTram(dir) {
  const p = pix(TRAM_W, TRAM_H), S = (x, y, col) => p.set(dir > 0 ? x : TRAM_W - 1 - x, y, col);
  const Y = ['#8a6410', '#b88a14', '#e0b01a', '#f4c82a', '#ffe070'];
  for (let y = 0; y < 4; y++) for (let x = [3, 2, 1, 0][y]; x < TRAM_W - [3, 2, 1, 0][y]; x++) S(x, y, ['#8a8478', '#ece8dc', '#d4d0c4', '#4a4438'][y]);   // the white roof
  for (let x = 18; x < 34; x++) { S(x, -1 + 1, '#3a3630'); }
  for (let y = 4; y < 14; y++) for (let x = 0; x < TRAM_W; x++) {             // the window band
    const pillar = x < 1 || x >= TRAM_W - 1 || (x >= 5 && x <= 54 && (x - 5) % 7 === 0) || x === 55;
    if (pillar) { S(x, y, Y[x < 2 || x > 59 ? 2 : 3]); continue; }
    const front = x > 55, rear = x < 5;
    S(x, y, front ? (y < 7 ? '#c8d0d8' : '#8a98a8') : rear ? '#e8b060' : y < 6 ? '#ffecb8' : y < 11 ? '#ffd890' : '#e8a850');
  }
  for (const bx of [12, 26, 33, 47]) for (let y = 9; y < 14; y++) for (let x = bx + 2; x < bx + 5; x++) if (y > 9 || x === bx + 3) S(x, y, '#2a1a14');   // passengers
  for (let x = 48; x < 55; x++) for (let y = 5; y < 25; y++) if (x === 48 || x === 54 || x === 51) S(x, y, Y[2]);   // the front door's frames
  for (let x = 0; x < TRAM_W; x++) S(x, 14, '#f4e8c4');                      // the belt rail
  for (let y = 15; y < 25; y++) for (let x = 0; x < TRAM_W; x++) {
    if (x >= 49 && x <= 53 && y < 24) { S(x, y, x === 51 ? Y[2] : '#e8b060'); continue; }   // the door's lit glass
    S(x, y, y === 19 ? Y[2] : y === 24 ? Y[1] : x < 1 || x >= TRAM_W - 1 ? Y[2] : Y[3]);
  }
  for (let x = 0; x < TRAM_W; x++) { S(x, 25, '#2a2622'); S(x, 26, '#3a3430'); }
  for (let y = 27; y < 33; y++) for (let x = 8; x < 54; x++) if (y < 29 || Math.abs(x - 15) < 5 || Math.abs(x - 46) < 5) S(x, y, '#1a1818');   // the truck
  for (const wx of [15, 46]) for (let y = 27; y < 33; y++) for (let x = wx - 3; x <= wx + 3; x++) {
    const d = Math.hypot(x - wx, y - 30);
    if (d < 3.2) S(x, y, d < 1 ? '#8a8480' : d > 2.4 ? '#3a3634' : '#242020');
  }
  const bx = dir > 0 ? TRAM_W - 12 : 1;                                      // the destination box over the cab, and the fleet number
  p.fill(bx, 0, 11, 5, '#141010'); textP(p, '28', bx + 2, 0, '#ffb030');
  textP(p, '547', dir > 0 ? 24 : TRAM_W - 36, 17, '#5a3e0e');
  const rb = dir > 0 ? 1 : TRAM_W - 12;
  p.fill(rb, 0, 11, 5, '#141010'); textP(p, '28', rb + 2, 0, '#c8801c');
  S(TRAM_W - 1, 20, '#fffae0'); S(TRAM_W - 1, 21, '#fffae0'); S(0, 20, '#ff3a2a');   // headlamp, tail lamp
  return p.done();
}
/** the tram at loop time t: { x (sprite's left column), dir, moving } or null. Up from the Sé at 26 s, a stop at the
 *  Portas do Sol (34-42 s), then round the corner into São Tomé; back down at 146 s without stopping */
function tramAt(t) {
  if (t >= 26 && t < 34) { const u = (t - 26) / 8; return { x: lerp(-66, 58, 1 - (1 - u) * (1 - u)), dir: 1, moving: true }; }
  if (t >= 34 && t < 42) return { x: 58, dir: 1, moving: false };
  if (t >= 42 && t < 53) { const u = (t - 42) / 11; return { x: lerp(58, 196, u * u), dir: 1, moving: u > 0.02 }; }
  if (t >= 144 && t < 158.5) { const u = (t - 144) / 14.5; return { x: lerp(210, -66, u * (0.6 + 0.4 * u)), dir: -1, moving: true }; }   // from far enough behind the corner house that its lamps start hidden: it noses out at 146 s
  return null;
}
const TRAM_BELLS = [26.6, 42.2, 146.4];
function drawTram(t) {
  const tr = tramAt(t);
  if (!tr) return;
  const X = Math.round(tr.x), front = tr.dir > 0 ? X + TRAM_W - 1 : X;
  ctx.save(); ctx.beginPath(); ctx.rect(0, 0, CH.x1, H); ctx.clip();          // it turns off behind the corner house, which is narrower than the tram
  halo(HL.tram, X + TRAM_W / 2, TRAM_Y + 10, 0.55);
  ctx.drawImage(tr.dir > 0 ? TRAM_C.r : TRAM_C.l, X, TRAM_Y);
  const base = tr.dir > 0 ? X + 16 : X + TRAM_W - 17, head = base - tr.dir * 14;   // the trolley pole trails behind
  const rows = TRAM_Y - 2 - WIRE;
  for (let k = 0; k <= rows; k++) {
    const x = Math.round(lerp(base, head, k / rows)), y = TRAM_Y - 1 - k;
    rect(ctx, x, y, 1, 1, '#1a1618'); rect(ctx, x + 1, y, 1, 1, '#8a8278');
  }
  rect(ctx, base - 2, TRAM_Y - 1, 5, 1, '#3a3430');                           // the pole's base on the roof
  rect(ctx, head - 1, WIRE + 1, 3, 1, '#5a544e');
  const nearHanger = HANGERS.some(h => Math.abs(head - h) < 1.5), rnd = hash(9, step(t, 2400), 7) < 0.05;
  if (tr.moving && (nearHanger || rnd)) {                                   // a spark where the pole's wheel meets the wire
    rect(ctx, head, WIRE, 1, 1, '#ffffff'); rect(ctx, head - tr.dir, WIRE - 1, 1, 1, '#bfe8ff'); rect(ctx, head + tr.dir, WIRE + 1, 1, 1, '#9ad8ff');
    halo(HL.spark, head, WIRE, 1);
  }
  halo(HL.head, front + tr.dir * 2, TRAM_Y + 20, 0.9); halo(HL.beam, front + tr.dir * 12, TRAM_Y + 26, 0.6);
  halo(HL.tail, tr.dir > 0 ? X : X + TRAM_W - 1, TRAM_Y + 20, 0.6);
  ctx.restore();
}
/* a woman with a shopping bag walks to the stop and waits there for the tram up (she boards at 37 s) */
function drawRider(t) {
  let x;
  if (t < 37.5) x = 122 - smooth(35.4, 37.4, t) * 10;                        // waiting, then stepping in at the door
  else if (t >= 168 && t < 178) x = lerp(66, 122, (t - 168) / 10);
  else if (t >= 178) x = 122;
  else return;
  x = Math.round(x);
  const walk = (t >= 168 && t < 178) || (t > 35.4 && t < 37.5), fr = walk && frac(t * 2.2) < 0.5;
  ctx.globalAlpha = t < 37.5 ? 1 - smooth(37, 37.5, t) : 1;
  rect(ctx, x, 172, 3, 3, '#2a1c16'); rect(ctx, x, 173, 3, 2, '#b07a58');   // head
  rect(ctx, x - 1, 175, 5, 9, '#5a2a3a'); rect(ctx, x - 1, 175, 1, 9, '#7a3a4a');   // coat
  rect(ctx, x - 1, 184, 5, 4, '#2a2a36');
  rect(ctx, x + (fr ? 0 : 1), 188, 1, 5, '#1a1a22'); rect(ctx, x + (fr ? 3 : 2), 188, 1, 5, '#1a1a22');
  rect(ctx, x + 4, 181, 2, 4, '#c8b48a');                                   // the bag
  ctx.globalAlpha = 1;
}

/* ================= The corner house: azulejo front, the tasca with fado in its window ================= */
let CH_WIN = [];
const chGlow = (x, y) => 0.55 * Math.max(0, 1 - Math.hypot((x - 234) / 30, (y - 164) / 28)) ** 1.3 + 0.35 * Math.max(0, 1 - Math.hypot((x - 204) / 28, (y - 184) / 20)) ** 1.5;
function renderCorner() {
  const p = pix(W, H), { x0, x1, top } = CH;
  for (let x = x0; x < x1; x++) {
    for (let y = top - 5; y < top; y++) {                                     // its roof
      const ins = Math.round((top - 1 - y) * 1.2);
      if (x < x0 + ins - 1 || x > x1 - ins) continue;
      let v = 0.36 + (x - x0) / (x1 - x0) * 0.1 + (y === top - 5 ? 0.2 : 0) - ((y - top) % 2 ? 0 : 0.08);
      p.set(x, y, pick(TILE, v + (hash(x, y, 201) - 0.5) * 0.08, x, y));
    }
    for (let y = top; y < PAR.top + 2; y++) {
      const g = chGlow(x, y), v = 0.32 + g + (y < top + 2 ? -0.16 : 0) - (x >= x1 - 2 ? 0.12 : 0);
      p.set(x, y, azulejo(x, y, v, g));
    }
  }
  for (let x = x0 - 1; x < x1 + 1; x++) { p.set(x, top, '#8a8078'); p.set(x, top + 1, '#4a4450'); }   // the eave's white cornice
  CH_WIN = [];
  for (const [wx, lit] of [[190, 1], [226, 0]]) {                              // the upper floor's windows
    p.fill(wx - 1, 144, 10, 14, '#b0a898'); p.fill(wx, 145, 8, 12, lit ? '#e89a48' : '#10131f');
    if (lit) { p.fill(wx, 145, 8, 3, '#ffd89a'); p.fill(wx, 145, 2, 12, '#c86a3a'); p.fill(wx + 6, 145, 2, 12, '#c86a3a'); }   // red curtains
    else { p.fill(wx - 3, 145, 2, 12, '#27493a'); p.fill(wx + 9, 145, 2, 12, '#27493a'); CH_WIN.push({ id: 950, x: wx, y: 145, w: 8, h: 12, col: '#e8a050', n: 3, off: 0.3, p: 0.5 }); }
    p.fill(wx, 150, 8, 1, lit ? '#b86a3a' : '#2a2c3a'); p.fill(wx + 3, 145, 1, 12, lit ? '#b86a3a' : '#2a2c3a');
  }
  for (let x = 187; x < 201; x++) { p.set(x, 158, '#1a1820'); if (x % 2) p.set(x, 159, '#1a1820'); p.set(x, 160, '#1a1820'); }   // the left window's iron balcony
  for (const gx of [188, 192, 196, 199]) { p.fill(gx, 156, 2, 2, '#9a4a2a'); p.set(gx, 155, '#e03a44'); p.set(gx + 1, 154, '#2e5a2e'); p.set(gx - 1, 155, '#2e5a2e'); }   // geraniums
  const { x: tx, y: ty, w: tw, h: th } = TASCA;
  p.fill(tx - 2, ty - 2, tw + 4, th + 4, '#c0b6a4'); p.fill(tx - 2, ty + th + 1, tw + 4, 1, '#8a8274');   // the stone frame
  p.fill(tx - 1, ty - 9, tw + 2, 7, '#2a1a14'); textP(p, 'FADO', tx + 2, ty - 8, '#f4e0b0');              // the painted sign
  p.fill(214, 166, 11, PAR.top - 164, '#c0b6a4'); p.fill(215, 167, 9, PAR.top - 165, '#b8682e'); p.fill(215, 167, 9, 3, '#ffc070');   // the open door
  p.fill(233, 158, 4, 1, '#1a1820'); p.set(236, 159, '#1a1820');             // the lantern's bracket
  return trim(p);
}
// the fado duo in the tasca window: a guitarist with a guitarra portuguesa, a singer in a black shawl
function drawTasca(t) {
  const { x: X, y: Y, w, h } = TASCA;
  for (let j = 0; j < h; j++) { ctx.fillStyle = j < 5 ? '#e0a058' : j < 11 ? '#c07838' : '#8a4c26'; ctx.fillRect(X, Y + j, w, 1); }
  rect(ctx, X + 8, Y + 1, 3, 2, '#ffe0a0');                                   // a lamp on the back wall
  rect(ctx, X + 1, Y + 11, w - 2, 1, '#5a3018');                              // a table's edge
  const bob = wave(t, 64, 0.1) > 0.6 ? 1 : 0, strum = hash(3, Math.floor(t * 6), 211) < 0.5 ? 0 : 1;
  rect(ctx, X + 2, Y + 3 + bob, 4, 4, '#1c1210'); rect(ctx, X + 3, Y + 4 + bob, 3, 3, '#c8906a'); rect(ctx, X + 3, Y + 4 + bob, 1, 1, '#1c1210');   // his head
  rect(ctx, X + 1, Y + 7, 6, h - 7, '#1a1414');                               // dark jacket
  rect(ctx, X + 4, Y + 10, 5, 4, '#c8843a'); rect(ctx, X + 5, Y + 10, 3, 1, '#e8a850'); rect(ctx, X + 6, Y + 11, 1, 1, '#3a200e');   // the pear-shaped body
  ctx.fillStyle = '#5a3a1c'; for (let k = 0; k < 5; k++) ctx.fillRect(X + 9 + k, Y + 10 - Math.round(k * 0.6), 1, 1);   // the neck
  rect(ctx, X + 13, Y + 6, 2, 2, '#e8d8a8');                                  // the fan of tuning screws
  rect(ctx, X + 11, Y + 8, 1, 1, '#c8906a');                                  // his fretting hand
  rect(ctx, X + 4, Y + 11 + strum, 2, 1, '#c8906a');                          // his plucking hand
  const tilt = wave(t, 20, 0.35), sing = hash(5, Math.floor(t * 3), 212) < 0.6;   // the singer, eyes closed, swaying
  const hx = X + 14 + (tilt > 0.5 ? 1 : tilt < -0.5 ? -1 : 0);
  rect(ctx, hx, Y + 1, 3, 3, '#140c10'); rect(ctx, hx, Y + 2, 3, 2, '#d8a080'); rect(ctx, hx - 1, Y + 1, 1, 4, '#140c10');
  if (sing) rect(ctx, hx + 1, Y + 3, 1, 1, '#5a2020');
  rect(ctx, X + 13, Y + 4, 6, h - 4, '#0c0a0e'); rect(ctx, X + 13, Y + 4, 6, 1, '#2a2430');   // the black shawl
  rect(ctx, X + 15, Y + 7, 2, 1, '#d8a080');                                  // her clasped hands
  halo(HL.tasca, X + w / 2, Y + h / 2, 0.85 + 0.1 * wave(t, 41, 0.2));
}
function drawCornerFx(t) {
  for (const w of CH_WIN) if (winOn(w, t)) rect(ctx, w.x, w.y, w.w, w.h, w.col);
  drawTasca(t);
  for (let k = 0; k < 9; k++) {                                               // the door's bead curtain, stirring
    const x = 215 + k, sw = Math.round(wave(t, 23, k * 0.12) * 0.8);
    ctx.fillStyle = k % 3 === 0 ? '#e84a3a' : k % 3 === 1 ? '#f0c040' : '#3a8a5a';
    for (let y = 170; y < 196; y += 2) ctx.fillRect(x + (y > 186 ? sw : 0), y, 1, 1);
  }
  rect(ctx, 234, 159, 3, 1, '#141218'); rect(ctx, 234, 160, 3, 4, '#ffe2a4'); rect(ctx, 235, 161, 1, 2, '#fff6dc'); rect(ctx, 234, 164, 3, 1, '#141218');   // the wall lantern
  halo(HL.wall, 235, 162, 0.95); halo(HL.door, 219, 186, 0.7);
  const rack = [[224, 158, '#e8e0d4', 4], [229, 158, '#b8404a', 5], [234, 158, '#4a6aa8', 3]];   // laundry on the rack under the window
  ctx.fillStyle = '#2a2630'; ctx.fillRect(223, 157, 14, 1);
  for (const [x, y, col, len] of rack) {
    const sw = wave(t, 29 + len * 3, x * 0.01) > 0.4 ? 1 : 0;
    rect(ctx, x, y, 3, len, col); rect(ctx, x + sw, y + len, 3 - sw, 1, mixHex(col, '#000000', 0.3));
  }
  drawCornerCat(t);
}
/* a black cat sitting on the corner house's roof, its tail hanging over the eave */
function drawCornerCat(t) {
  const x = 200, y = CH.top - 1, look = frac(t * 6 / LOOP) > 0.7 ? 1 : 0;
  rect(ctx, x - 2, y - 4, 5, 4, '#0a080e'); rect(ctx, x - 1 + look, y - 7, 4, 3, '#0a080e'); rect(ctx, x - 1 + look, y - 8, 1, 1, '#0a080e'); rect(ctx, x + 2 + look, y - 8, 1, 1, '#0a080e');
  if (!(frac(t * 45 / LOOP) > 0.94)) { rect(ctx, x + look, y - 6, 1, 1, '#d8f060'); rect(ctx, x + 2 + look, y - 6, 1, 1, '#d8f060'); }
  const sw = wave(t, 30, 0.2) * 1.4 + wave(t, 89, 0.5) * 0.6;
  for (let j = 0; j < 7; j++) rect(ctx, x - 3 - Math.round(Math.max(0, sw) * j / 6), y + j, 1, 1, '#0a080e');
}

/* ================= The terrace: calçada waves, the azulejo parapet, a bench, the lamp, the pergola ================= */
const LIME = ['#24242e', '#32323e', '#44424e', '#5a5660', '#726c74', '#8c848a', '#a69c9c', '#beb2ac', '#d4c8bc'];
const BASALT = ['#08080e', '#0e0e16', '#15151e', '#1e1e28', '#282832'];
const lampLight = (x, y) => 0.75 * Math.max(0, 1 - Math.hypot((x - LAMP.x) / 120, (y - 232) / 46)) ** 1.5
  + 0.35 * Math.max(0, 1 - Math.hypot((x - 130) / 50, (y - 226) / 22)) ** 1.5;   // the lamp, the cart's fire
function floorPx(x, y) {
  const z = 60 / (y - 186), u = (x - 240) * z / 60, L = lampLight(x, y);
  // the "wide sea" waves of black basalt in white limestone; far off, where a wave is only a few rows deep, they fade
  // into an even grey instead of aliasing
  const band = frac(z * 3.2 + 0.26 * Math.sin(u * 4.4) + 0.08 * Math.sin(u * 11.3)), rows = 60 / (3.2 * z * z);
  const ink = smooth(0.04, 0.12, Math.min(band, 0.36 - band)) * smooth(3, 7, rows);
  const sz = 16, si = Math.floor(u * sz + (Math.floor(z * sz) & 1) * 0.5), sj = Math.floor(z * sz);
  const fu = u * sz + (sj & 1) * 0.5 - si, fz = z * sz - sj, big = 60 / z / sz;
  const joint = big > 2.6 && (fu < 0.9 / big || fz < 0.8 / (big * 1.2));
  const wet = 0.14 * Math.max(0, 1 - Math.abs(x - LAMP.x - (y - 232) * 0.1) / 6) * smooth(236, 262, y) * (hash(x, y >> 1, 222) < 0.6 ? 1 : 0);   // the lamp's sheen on the damp stones
  const n = (hash(si, sj, 221) - 0.5) * 0.1 - (joint ? 0.16 : 0) + wet;
  return band < 0.36 && ink > bayer(x, y) ? pick(BASALT, 0.34 + L * 0.9 + n, x, y) : pick(LIME, 0.16 + L * 0.95 + n - 0.06 * (1 - smooth(3, 7, rows)), x, y);
}
function paintPanel(p, x0, x1, seed) {                                      // a blue-and-white panel: the river front before 1755
  const r = rng(seed), blocks = [];
  for (let x = x0 + 3; x < x1 - 4;) { const w = 3 + Math.floor(r() * 5); blocks.push({ x, w, h: 2 + Math.floor(r() * 4), tower: r() < 0.18 }); x += w; }
  const ships = [x0 + 8 + Math.floor(r() * 12), x1 - 16 - Math.floor(r() * 10)];
  for (let y = PAR.top + 3; y < PAR.bot - 3; y++) for (let x = x0; x < x1; x++) {
    const L = lampLight(x, y), v = 0.34 + L * 0.9, warm = L > 0.22 ? 1 : 0;
    let m = 0;                                                                // 0 white, 1 light blue, 2 cobalt
    if (y === PAR.top + 3 || y === PAR.bot - 4 || x === x0 || x === x1 - 1) m = 2;
    else if (y === PAR.top + 4 || y === PAR.bot - 5 || x === x0 + 1 || x === x1 - 2) m = 0;
    else if (y === PAR.top + 5 || y === PAR.bot - 6) m = (x + y) % 3 ? 1 : 2;   // a scrolled border
    else {
      const hy = PAR.top + 15;
      const b = blocks.find(q => x >= q.x && x < q.x + q.w);
      if (b && y < hy && y >= hy - b.h - (b.tower ? 3 : 0) && (!b.tower || y >= hy - b.h || x === b.x + (b.w >> 1))) m = (x === b.x || y === hy - b.h) ? 2 : (x + y) % 2 ? 1 : 0;
      else if (y === hy) m = 2;
      else if (y > hy && Math.sin(x * 0.9 + y * 2.1) > 0.55) m = 1;              // waves
      for (const sx of ships) {                                              // a caravel: hull, mast and sails
        if (y >= hy - 1 && y <= hy && x >= sx - 3 && x <= sx + 3) m = 2;
        else if (y < hy - 1 && y >= hy - 7 && Math.abs(x - sx) <= Math.floor((y - (hy - 8)) * 0.5)) m = x === sx ? 2 : 0;
      }
    }
    p.set(x, y, pick((warm ? AZ_WARM : AZ_COOL)[m], v, x, y));
  }
}
function renderTerrace() {
  const p = pix(W, H);
  for (let y = 199; y < H; y++) for (let x = 0; x < W; x++) p.set(x, y, floorPx(x, y));
  for (let x = 0; x < ST_END + 6; x++) { p.set(x, 199, pick(LIME, 0.5 + lampLight(x, 200), x, 199)); p.set(x, 200, '#141420'); }   // the kerb
  const r = rng(231);                                                         // fallen leaves blown against the kerb and the parapet
  for (let k = 0; k < 90; k++) {
    const x = Math.floor(r() * W), y = r() < 0.5 ? 201 + Math.floor(r() * 6) : 233 + Math.floor(r() * 30);
    const c = ['#e0b040', '#c89030', '#a86a24', '#f0cc60'][Math.floor(r() * 4)];
    p.set(x, y, c); if (y > 240 && r() < 0.6) p.set(x + 1, y, mixHex(c, '#3a2410', 0.3));
  }
  // the parapet: a limestone coping, azulejo panels between pilasters, a plinth
  for (let x = PAR.x0; x < W; x++) {
    for (let y = PAR.top; y < PAR.bot; y++) {
      const L = lampLight(x, y), e = (x - PAR.x0) % 64;
      if (y < PAR.top + 3) { p.set(x, y, pick(LIME, (y === PAR.top ? 0.62 : y === PAR.top + 1 ? 0.42 : 0.18) + L * 0.9, x, y)); continue; }
      if (y >= PAR.bot - 3 || e < 4) { p.set(x, y, pick(LIME, 0.3 + L * 0.8 - (e === 3 ? 0.12 : 0) + (hash(x, y, 232) - 0.5) * 0.06, x, y)); continue; }
    }
  }
  for (let x0 = PAR.x0 + 4; x0 < W; x0 += 64) paintPanel(p, x0, Math.min(W + 4, x0 + 60), 240 + x0);
  // the parapet's end pillar with the lamp post on it
  for (let y = 190; y < PAR.bot; y++) for (let x = 162; x < 175; x++) {
    const L = lampLight(x, y) + 0.2;
    p.set(x, y, pick(LIME, (y < 192 ? 0.66 : 0.4) + L * 0.6 - (x > 172 ? 0.16 : x < 164 ? 0.06 : 0) + (y === 192 ? -0.2 : 0), x, y));
  }
  for (let y = LAMP.y + 3; y < 190; y++) { p.set(LAMP.x, y, '#1a1820'); p.set(LAMP.x + 1, y, y % 6 === 0 ? '#3a3440' : '#121018'); if (y === 176 || y === 186) { p.set(LAMP.x - 1, y, '#1a1820'); p.set(LAMP.x + 2, y, '#1a1820'); } }
  // the stone bench, its back to us, azulejos on the backrest
  for (let y = 204; y < 234; y++) for (let x = 4; x < 62; x++) {
    const L = lampLight(x, y);
    if (y < 206) p.set(x, y, pick(LIME, 0.5 + L, x, y));
    else if (y < 218) p.set(x, y, x < 6 || x > 59 ? pick(LIME, 0.36 + L, x, y) : azulejo(x, y, 0.36 + L * 0.8, L > 0.2 ? 1 : 0));
    else if (y < 221 || (x < 12 || x > 53)) p.set(x, y, pick(LIME, 0.3 + L * 0.8 - (y > 220 ? 0.1 : 0), x, y));
  }
  // the pergola's column at the right edge, its beam overhead, vines hanging from them
  for (let y = 0; y < PAR.bot; y++) for (let x = COL.x0; x < COL.x1; x++) {
    let v = 0.42 + (x === COL.x0 ? 0.14 : x === COL.x0 + 1 ? 0.06 : x > COL.x1 - 4 ? -0.14 : 0) + (vnoise(x / 2, y / 6, 251) - 0.5) * 0.08;
    if (y > 186 && y < 190) v += y === 187 ? 0.18 : -0.12;                    // a moulded base
    p.set(x, y, pick(LIME, v, x, y));
  }
  for (let x = 318; x < W; x++) for (let y = 0; y < 5; y++) p.set(x, y, y === 4 ? '#3a3036' : y === 3 ? '#2a2228' : '#1e181c');
  for (let x = 322; x < W; x += 20) p.fill(x, 5, 3, 2, '#2a2228');
  const vr = rng(261), leaf = ['#0e1a14', '#15261c', '#1e3424', '#2a4430', '#6a6a2a', '#8a7a30'];
  // solid clumps of foliage (dark underneath, the moon catching the tops), a few late bougainvillea bracts
  const clump = (cx, cy, rx, ry) => {
    for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let x = Math.floor(cx - rx); x <= cx + rx; x++) {
      const d = ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2;
      if (d > 1 || hash(x, y, 262) < 0.18 + d * 0.3) continue;
      const v = 0.3 + (cy - y) / ry * 0.25 + (vnoise(x / 2, y / 2, 263) - 0.5) * 0.5;
      p.set(x, y, hash(x, y, 264) < 0.035 ? (hash(x, y, 265) < 0.5 ? '#b83a7a' : '#8a2a5a') : hash(x, y, 266) < 0.07 ? leaf[4 + (x & 1)] : pick(leaf.slice(0, 4), v, x, y));
    }
  };
  for (let x = 324; x < W; x += 4 + Math.floor(vr() * 5)) {                  // hanging from the beam
    const len = 3 + Math.floor(vr() * vr() * 18);
    clump(x, 5, 3.5, 2.6);
    for (let y = 7; y < 5 + len; y += 2) clump(x + Math.round(Math.sin(y * 0.35 + x) * 1.2), y, 1.4, 1.6);
  }
  for (let y = 6; y < 140; y += 3) clump(COL.x0 + 1 + Math.round(Math.sin(y * 0.2) * 1.5), y, 2.6 - y / 90, 2);   // climbing the column
  return p.done();
}

/* ================= The chestnut roaster's cart (static), its fire, smoke and the castanheiro ================= */
function renderCart() {
  const p = pix(W, H), { x: X, y: G } = CART;
  const L = (x, y) => lampLight(x, y);
  for (const [wx, wy] of [[X + 9, G - 7], [X + 36, G - 7]]) for (let y = wy - 7; y <= wy + 7; y++) for (let x = wx - 7; x <= wx + 7; x++) {   // spoked wheels
    const d = Math.hypot(x - wx, y - wy), a = Math.atan2(y - wy, x - wx);
    if (d > 6.8) continue;
    if (d > 5.6) p.set(x, y, pick(['#0e0c10', '#2a2426', '#4a4044'], 0.3 + L(x, y), x, y));
    else if (d < 1.2) p.set(x, y, '#5a5054');
    else if (Math.abs(Math.sin(a * 3)) < 0.18) p.set(x, y, '#2a2426');
  }
  for (let y = G - 26; y < G - 12; y++) for (let x = X; x < X + 45; x++) {       // the painted body
    let v = 0.32 + L(x, y) * 0.8 + (y === G - 26 ? 0.2 : 0) - (x > X + 42 ? 0.12 : 0);
    p.set(x, y, pick(['#0e1a14', '#16281e', '#1e3a2a', '#2a4e38', '#3a6a4c'], v, x, y));
  }
  p.fill(X + 2, G - 23, 38, 7, '#e8dcc0'); textP(p, 'CASTANHAS', X + 3, G - 22, '#5a1a14');   // its sign
  for (let y = G - 36; y < G - 26; y++) for (let x = X + 6; x < X + 39; x++) {   // the roaster's drum
    const u = (x - X - 6) / 33, q = (y - (G - 36)) / 10;                         // lit from the lamp above: bright top, dark belly
    let v = 0.25 + 0.45 * (1 - q) * Math.sin(Math.PI * (0.15 + u * 0.7)) + L(x, y) * 0.4;
    if (y === G - 36) v += 0.25; else if (y === G - 27) v -= 0.2;
    if ((x - X - 6) % 11 === 0) v += 0.22;                                       // the barrel's hoops
    p.set(x, y, pick(['#141214', '#221e20', '#322c2e', '#48403e', '#625650', '#847466', '#a89480'], v, x, y));
  }
  for (let x = X + 8; x < X + 37; x++) { p.set(x, G - 37, '#8a4a2a'); p.set(x, G - 38, '#6a3420'); }   // the clay tray
  p.fill(X + 39, G - 32, 3, 1, '#4a4040'); p.fill(X + 41, G - 32, 1, 4, '#4a4040'); p.fill(X + 41, G - 29, 3, 1, '#6a5a50');   // the drum's crank
  const r = rng(271);
  for (let k = 0; k < 26; k++) { const x = X + 9 + Math.floor(r() * 27), y = G - 40 + Math.floor(r() * 2); p.set(x, y, r() < 0.3 ? '#e8c070' : r() < 0.5 ? '#8a4a24' : '#5a2a14'); }   // chestnuts
  for (let y = CHIM.y; y < G - 38; y++) { p.set(CHIM.x - 1, y, '#1a1618'); p.set(CHIM.x, y, '#3a3234'); p.set(CHIM.x + 1, y, '#141012'); }   // the smoke stack
  p.fill(CHIM.x - 2, CHIM.y - 1, 5, 1, '#2a2426'); p.fill(CHIM.x - 1, CHIM.y - 2, 3, 1, '#2a2426');
  for (let k = 0; k < 4; k++) { p.fill(X + 40, G - 43 + k * 2, 5, 2, k % 2 ? '#d8d0c0' : '#bcb4a4'); p.set(X + 41 + k, G - 42 + k * 2, '#6a6660'); }   // newspaper cones
  p.fill(X - 8, G - 20, 9, 1, '#2a2426'); p.fill(X - 9, G - 21, 2, 3, '#3a3234');   // the handles
  p.fill(X + 22, G - 12, 2, 12, '#1a1618');                                   // its prop leg
  return trim(p);
}
const LID = [70, 150, 222];                                                   // the lid comes off: a burst of smoke
const lidUp = t => LID.some(a => t >= a && t < a + 3.2);
function drawCartFx(t) {
  const { x: X, y: G } = CART;
  for (let k = 0; k < 8; k++) {                                               // embers glowing through the firebox's slits
    const h = hash(k, Math.floor(t * 8), 281);
    rect(ctx, X + 24 + k * 2, G - 14, 1, 2, h < 0.3 ? '#ffe070' : h < 0.7 ? '#ff8a2a' : '#c83a14');
  }
  halo(HL.ember, X + 31, G - 14, 0.8 + 0.2 * hash(2, Math.floor(t * 6), 282));
  halo(HL.roast, X + 22, G - 39, 0.7 + 0.15 * wave(t, 70, 0.3));
  if (lidUp(t)) { rect(ctx, X + 12, G - 46, 16, 1, '#3a3234'); rect(ctx, X + 13, G - 47, 14, 1, '#221e20'); }   // the lid held up
}
function drawVendor(t) {
  const X = 92, G = CART.y, stir = frac(t * 17 / LOOP) < 0.36 || lidUp(t);
  const mv = stir ? (hash(4, Math.floor(t * 3), 291) < 0.5 ? 0 : 1) : 0;
  rect(ctx, X + 2, G - 16, 3, 16, '#1a1a22'); rect(ctx, X + 6, G - 16, 3, 16, '#16161e');   // trousers
  rect(ctx, X + 1, G - 34, 9, 19, '#26262e'); rect(ctx, X + 9, G - 34, 1, 19, '#4a3a34');    // coat, lit by the fire
  rect(ctx, X + 2, G - 36, 7, 3, '#8a2a2a');                                  // scarf
  rect(ctx, X + 3, G - 43, 5, 7, '#c89070'); rect(ctx, X + 8, G - 40, 1, 2, '#c89070');     // face in profile
  rect(ctx, X + 2, G - 45, 7, 3, '#2a2a30'); rect(ctx, X + 7, G - 43, 3, 1, '#2a2a30');     // flat cap and its peak
  rect(ctx, X + 7, G - 41, 1, 1, '#1a1010');
  if (stir) {                                                                 // turning the chestnuts with his scoop
    ctx.fillStyle = '#26262e'; for (let k = 0; k < 8; k++) ctx.fillRect(X + 9 + k, G - 31 - Math.round(k * 0.5) - mv, 1, 2);
    rect(ctx, X + 17, G - 36 - mv, 2, 2, '#c89070'); rect(ctx, X + 19, G - 38 - mv, 5, 1, '#8a8480');
  } else { rect(ctx, X + 8, G - 26, 2, 3, '#c89070'); }
  if (lidUp(t)) rect(ctx, X + 10, G - 44, 2, 12, '#26262e');
}

/* ================= A couple leaning on the parapet, a gull on it, gulls wheeling over the river ================= */
const COUPLE_X = 296, COUPLE_POINT = [[62, 69], [101.5, 106]], LEAN = [[24, 92], [128, 196]];
/** a figure from rows [y, x0, x1, colour] spans, left rim lit warm by the lamp */
function spans(list, dx = 0, dy = 0, rim = null) {
  for (const [y, x0, x1, col] of list) {
    rect(ctx, x0 + dx, y + dy, x1 - x0, 1, col);
    if (rim) rect(ctx, x0 + dx, y + dy, 1, 1, rim);
  }
}
function drawCouple(t) {
  const X = COUPLE_X, lean = inEvent(t, LEAN) ? smooth(0, 1.5, Math.min(...LEAN.map(([a, b]) => t >= a && t < b ? Math.min(t - a, b - t) : 9))) : 0;
  const point = inEvent(t, COUPLE_POINT), breath = wave(t, 60, 0.2) > 0.7 ? 1 : 0;
  // her, seen from behind: long brown hair over a camel coat, a red scarf, her elbows on the coping
  const body = [];
  for (let y = 203; y < 238; y++) {
    const hw = y < 207 ? 4 + (y - 203) * 0.5 : y < 214 ? 6 : 6 + (y - 214) * 0.08;
    body.push([y, Math.round(X + 7 - hw), Math.round(X + 7 + hw), y < 205 ? '#a82a34' : '#5e4230']);
  }
  spans(body, 0, 0, '#9a6a48');
  rect(ctx, X + 1, 214, 1, 22, '#4a3424'); rect(ctx, X + 12, 214, 1, 22, '#3a281e');   // the coat's folds
  rect(ctx, X + 3, 238, 2, 8, '#1a1418'); rect(ctx, X + 9, 238, 2, 8, '#1a1418');       // her legs
  rect(ctx, X - 1, 205, 4, 3, '#5e4230'); rect(ctx, X - 1, 205, 1, 3, '#9a6a48');       // an elbow on the coping
  const hx = X + 4 + Math.round(lean * 2), hy = 195 + Math.round(lean);
  spans([[hy, hx + 1, hx + 6, '#3a2418'], [hy + 1, hx, hx + 7, '#3a2418'], [hy + 2, hx, hx + 7, '#3a2418'], [hy + 3, hx, hx + 7, '#3a2418'],
    [hy + 4, hx, hx + 7, '#3a2418'], [hy + 5, hx, hx + 7, '#342016'], [hy + 6, hx - 1, hx + 8, '#342016'], [hy + 7, hx - 1, hx + 8, '#2e1c14'],
    [hy + 8, hx - 1, hx + 8, '#2e1c14'], [hy + 9, hx, hx + 7, '#2e1c14']], 0, 0, '#7a4a2c');
  // him: a navy coat, broad shoulders, short dark hair, an ear catching the lamp
  const B = breath, hb = [];
  for (let y = 201 + B; y < 237; y++) {
    const hw = y < 204 + B ? 4 + (y - 201 - B) * 1.5 : y < 214 ? 8 : 7.5;
    hb.push([y, Math.round(X + 21 - hw), Math.round(X + 21 + hw), y < 203 + B ? '#2a2e40' : '#1a1e2c']);
  }
  spans(hb, 0, 0, '#4a4a5c');
  rect(ctx, X + 20, 204 + B, 1, 30, '#141824');                               // the coat's seam
  rect(ctx, X + 16, 237, 3, 9, '#141418'); rect(ctx, X + 23, 237, 3, 9, '#141418');
  spans([[193 + B, X + 18, X + 24, '#1a1210'], [194 + B, X + 17, X + 25, '#1a1210'], [195 + B, X + 17, X + 25, '#1e1612'], [196 + B, X + 17, X + 25, '#1e1612'],
    [197 + B, X + 17, X + 25, '#1e1612'], [198 + B, X + 17, X + 25, '#241a14'], [199 + B, X + 18, X + 24, '#3a2a22'], [200 + B, X + 19, X + 23, '#3a2a22']], 0, 0, '#5a3a2a');
  rect(ctx, X + 16, 196 + B, 1, 2, '#b07a5a'); rect(ctx, X + 25, 196 + B, 1, 2, '#6a4a3a');   // ears
  if (point) {                                                                // pointing out at the river
    ctx.fillStyle = '#1a1e2c'; for (let k = 0; k < 10; k++) ctx.fillRect(X + 28 + k, 204 - Math.round(k * 0.7), 2, 2);
    rect(ctx, X + 38, 196, 2, 2, '#b07a5a');
  } else { rect(ctx, X + 28, 205, 4, 3, '#1a1e2c'); rect(ctx, X + 28, 205, 4, 1, '#2a2e40'); }
}
const GULL_CALL = 205.8;
function drawParapetGull(t) {
  const x = 392, y = PAR.top, call = t >= GULL_CALL && t < GULL_CALL + 1.6, turn = frac(t * 10 / LOOP) > 0.6;
  rect(ctx, x + 1, y - 1, 1, 1, '#d8a860'); rect(ctx, x + 4, y - 1, 1, 1, '#d8a860');   // legs
  rect(ctx, x, y - 5, 7, 4, '#dcdce4'); rect(ctx, x + 1, y - 5, 6, 2, '#8a90a4'); rect(ctx, x + 6, y - 4, 2, 1, '#2a2a34');   // body, grey back, black wingtips
  const hx = turn ? x + 5 : x - 1, dir = turn ? 1 : -1, up = call ? 2 : 0;
  rect(ctx, hx, y - 7 - up, 3, 3, '#ececf2');
  rect(ctx, hx + (dir > 0 ? 3 : -1), y - 6 - up, 1, 1, '#e8c040');
  if (call) rect(ctx, hx + (dir > 0 ? 3 : -1), y - 5 - up, 1, 1, '#c8301a');
  rect(ctx, hx + (dir > 0 ? 2 : 0), y - 6 - up, 1, 1, '#1a1a20');
}
const GULLS = [{ k: 5, ph: 0.1, cx: 372, cy: 106, rx: 46, ry: 9, dir: 1 }, { k: 4, ph: 0.6, cx: 300, cy: 92, rx: 30, ry: 7, dir: -1 }];
const GULL_F = [['#...#', '.#.#.', '..#..'], ['##.##', '..#..'], ['..#..', '.#.#.', '#...#']];
const gullPos = (g, i, t) => { const a = TAU * (g.k * t / LOOP + g.ph) * g.dir; return [Math.round(g.cx + Math.cos(a) * g.rx), Math.round(g.cy + Math.sin(a) * g.ry + wave(t, 29 + i * 7, i * 0.3) * 2)]; };
function drawGulls(t) {
  GULLS.forEach((g, i) => {
    const [x, y] = gullPos(g, i, t), flap = frac(t * 1.2 + i * 0.37), fr = flap < 0.5 ? 1 : flap < 0.68 ? 0 : flap < 0.84 ? 2 : 1;
    ctx.fillStyle = '#ece2d4';
    GULL_F[fr].forEach((row, j) => { for (let q = 0; q < 5; q++) if (row[q] === '#') ctx.fillRect(x - 2 + q, y - 1 + j, 1, 1); });
  });
}
/* a cat strolling along a long ridge in Alfama, then sitting a while at the far end */
function drawRidgeCat(t) {
  if (!CAT_RIDGE) return;
  const { x0, x1, y } = CAT_RIDGE;
  let u, walking = false;
  if (t < 62) u = 0; else if (t < 96) { u = (t - 62) / 34; walking = true; } else if (t < 180) u = 1; else if (t < 214) { u = 1 - (t - 180) / 34; walking = true; } else u = 0;
  const dir = t >= 150 && t < 214 ? -1 : 1, x = Math.round(lerp(x0, x1, u));
  ctx.fillStyle = '#0a080e';
  if (walking) {
    ctx.fillRect(x - 2, y - 3, 5, 2); ctx.fillRect(x + dir * 3 - (dir < 0 ? 1 : 0), y - 4, 2, 2);
    const s = frac(t * 2.5) < 0.5 ? 0 : 1; ctx.fillRect(x - 2 + s, y - 1, 1, 1); ctx.fillRect(x + 2 - s, y - 1, 1, 1);
    ctx.fillRect(x - dir * 3, y - 4, 1, 1); ctx.fillRect(x - dir * 4, y - 5, 1, 1);
  } else {
    ctx.fillRect(x - 1, y - 4, 3, 4); ctx.fillRect(x - 1 + (dir > 0 ? 1 : -1), y - 6, 3, 2);
    if (frac(t * 56 / LOOP) > 0.1) { rect(ctx, x + (dir > 0 ? 1 : -1), y - 5, 1, 1, '#d8f060'); ctx.fillStyle = '#0a080e'; }
    ctx.fillRect(x - dir * 2, y - 1, 1, 1); ctx.fillRect(x - dir * 3, y - 1 - (wave(t, 40) > 0 ? 1 : 0), 1, 1);
  }
}

/* ================= The lamp on the parapet's pillar, an old man reading on the bench ================= */
const LAMP_FLICK = [[133.4, 134.6]];
function drawLamp(t) {
  const lv = (0.95 + 0.05 * wave(t, 1700, 0.3)) * flick(t, LAMP_FLICK, 9), { x, y } = LAMP;
  rect(ctx, x, y - 14, 2, 1, '#2a2630'); rect(ctx, x - 1, y - 13, 4, 1, '#1a1820'); rect(ctx, x - 3, y - 12, 8, 2, '#221e26');   // the crown
  for (let j = 0; j < 9; j++) {                                               // six-sided glass, wider at the top
    const hw = j < 3 ? 3 : j < 6 ? 2 : 2, x0 = x - hw + (j >= 6 ? 0 : 0);
    rect(ctx, x0, y - 10 + j, 2 * hw + 2, 1, '#1a1820');
    rect(ctx, x0 + 1, y - 10 + j, 2 * hw, 1, lv > 0.5 ? (j > 1 && j < 7 ? '#fff6dc' : '#ffe2a4') : '#7a5838');
    rect(ctx, x, y - 10 + j, 2, 1, lv > 0.5 ? '#ffe8b4' : '#6a4a30');
  }
  rect(ctx, x - 2, y - 1, 6, 1, '#1a1820'); rect(ctx, x - 1, y, 4, 1, '#221e26'); rect(ctx, x, y + 1, 2, 2, '#1a1820');
  halo(HL.lamp, x + 1, y - 5, lv); halo(HL.lampCore, x + 1, y - 6, lv);
}
/* an old man on the stone bench, seen from behind like everyone on the terrace: a flat cap over white hair, his
   jacket lit on the right by the wall lantern above him, an open newspaper held up wide of his shoulders (he turns a
   page every 18 s and nods over it now and then), his legs under the seat and his cane hooked over the bench's end */
function drawBenchMan(t) {
  const cx = 31, turn = frac(t * 13 / LOOP) > 0.92, nod = wave(t, 16, 0.4) > 0.8 ? 1 : 0;
  rect(ctx, cx - 6, 223, 4, 4, '#4a4250'); rect(ctx, cx + 3, 223, 4, 4, '#4a4250');          // trouser legs under the seat
  rect(ctx, cx - 6, 223, 1, 4, '#5e5462'); rect(ctx, cx + 6, 223, 1, 4, '#6a5a5a');
  rect(ctx, cx - 7, 227, 5, 2, '#0e0c12'); rect(ctx, cx + 3, 227, 5, 2, '#0e0c12');            // his shoes, the heels catching the lantern
  rect(ctx, cx + 7, 227, 1, 2, '#7a6050'); rect(ctx, cx - 3, 227, 1, 1, '#4a3c3a');
  ctx.fillStyle = '#2e2018';                                                  // the cane, its crook over the bench's end
  for (let y = 205; y < 233; y++) ctx.fillRect(56 + Math.round((y - 205) * 0.08), y, 1, 1);
  rect(ctx, 54, 202, 3, 1, '#4a3424'); rect(ctx, 54, 203, 1, 2, '#2e2018'); rect(ctx, 56, 202, 1, 3, '#2e2018'); rect(ctx, 55, 202, 2, 1, '#8a6a48');
  // the newspaper: its back page, darker on the left away from the lantern, a fold down the middle, columns of print
  const pw = turn ? 38 : 34, px0 = cx - (pw >> 1);
  rect(ctx, px0, 189, cx - px0, 13, '#b4a990'); rect(ctx, cx, 189, px0 + pw - cx, 13, '#d6caae');
  rect(ctx, px0, 189, pw, 1, '#ece0c6'); rect(ctx, cx, 189, 1, 13, '#7a705e');
  rect(ctx, px0 + 2, 191, cx - px0 - 4, 1, '#4e483e'); rect(ctx, cx + 2, 191, px0 + pw - cx - 4, 1, '#5a5246');   // headlines
  for (let k = 0; k < 4; k++) for (let c = 0; c < pw - 4; c += 6) {
    const x = px0 + 2 + c;
    if (x !== cx && x + 4 <= px0 + pw - 2) rect(ctx, x, 193 + k * 2, 4, 1, x < cx ? '#8e846e' : '#a89c82');
  }
  rect(ctx, px0 + 2, 193, 4, 5, '#6e6656'); rect(ctx, px0 + pw - 6, 193, 4, 4, '#857c6a');   // photographs
  if (turn) { rect(ctx, px0 + pw - 6, 186, 6, 6, '#ece0c6'); rect(ctx, px0 + pw - 6, 191, 6, 1, '#a89c82'); }   // a page turning over
  // his arms out to the paper's edges, his hands holding it
  ctx.fillStyle = '#2c262c';
  for (let k = 0; k < 5; k++) { ctx.fillRect(cx - 10 - k, 195 + k, 2, 2); ctx.fillRect(cx + 9 + k, 195 + k, 2, 2); }
  rect(ctx, cx + 10, 195, 1, 1, '#6a5648'); rect(ctx, cx + 11, 196, 1, 1, '#6a5648');       // the lantern on his right sleeve
  rect(ctx, cx - 16, 199, 2, 2, '#8a6450'); rect(ctx, cx + 15, 199, 2, 2, '#c08a6a');
  // a hunched back in a tweed jacket, its right side catching the lantern
  const back = [];
  for (let y = 193 + nod; y < 204; y++) {
    const hw = y < 194 + nod ? 5 : y < 195 + nod ? 7 : 8;
    back.push([y, cx - hw, cx + hw + 1, y < 195 + nod ? '#4a4044' : '#342e34']);
  }
  spans(back);
  for (const [y, x0, x1] of back) rect(ctx, x1 - 1, y, 1, 1, '#7a6458');
  rect(ctx, cx, 196 + nod, 1, 8, '#262028');                                  // the jacket's seam
  // white hair under a flat cap, his ears and neck
  const hy = 186 + nod;
  rect(ctx, cx - 3, hy, 7, 1, '#5e5248'); rect(ctx, cx - 4, hy + 1, 9, 2, '#3e3632'); rect(ctx, cx + 3, hy + 1, 2, 1, '#7a6a58');   // the cap
  rect(ctx, cx - 4, hy + 3, 9, 3, '#d4cec4'); rect(ctx, cx - 4, hy + 3, 3, 3, '#a8a29a');   // white hair, shadowed on the left
  rect(ctx, cx - 5, hy + 3, 1, 2, '#8a6450'); rect(ctx, cx + 5, hy + 3, 1, 2, '#d09a78');   // ears
  rect(ctx, cx - 2, hy + 6, 5, 1, '#9a7058');                                  // his neck above the collar
}

/* ================= Chestnut smoke: white puffs out of the cart's stack, warm where they pass the lamp ================= */
function makePuff(r, col) {
  const s = r * 2 + 1, p = pix(s, s), c = hex(col);
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) {
    const d = Math.hypot(x - r, y - r) / (r + 0.5);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 0.8) * 4 + bayer(x, y)) / 4;
    if (q > 0) p.set(x, y, c, Math.round(Math.min(1, q) * 255));
  }
  return p.done();
}
const PUFFS = 22;
function drawSmoke(t) {
  for (let k = 0; k < PUFFS; k++) {
    const { u } = cycle(t, 40, k / PUFFS);
    const x = CHIM.x + u * 28 + 2.5 * Math.sin(TAU * (u * 1.3 + k * 0.37)) + 2.5 * wave(t, 7, k * 0.13) * u, y = CHIM.y - 3 - u * 64;
    const rad = Math.min(11, 2 + Math.round(u * 10)), a = 0.85 * Math.pow(1 - u, 1.05) * smooth(0, 0.04, u);
    if (a < 0.02) continue;
    const warm = Math.hypot(x - LAMP.x, y - LAMP.y + 6) < 28 ? 1 : 0;
    ctx.globalAlpha = a; ctx.drawImage(PUFF_C[warm][rad], Math.round(x) - rad, Math.round(y) - rad);
  }
  for (const a0 of LID) {                                                     // the burst off the open tray
    const s = t - a0;
    if (s < 0 || s >= 7) continue;
    for (let k = 0; k < 8; k++) {
      const u = (s - k * 0.3) / 4;
      if (u < 0 || u >= 1) continue;
      const x = CART.x + 12 + k * 3 + u * 16, y = CART.y - 46 - u * 44, rad = Math.min(8, 2 + Math.round(u * 6));
      ctx.globalAlpha = 0.45 * (1 - u); ctx.drawImage(PUFF_C[0][rad], Math.round(x) - rad, Math.round(y) - rad);
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= Yellow plane-tree leaves drifting down at three depths ================= */
const LEAF_PAL = [
  { base: '#e8c040', dark: '#a07818', light: '#fae488', back: '#c8a038' },
  { base: '#d8a030', dark: '#94621a', light: '#f4c860', back: '#b88430' },
  { base: '#c8b848', dark: '#7a7020', light: '#e8dc80', back: '#a89a38' },
];
const LEAF_SH = [['11', '10'], ['0110', '1111', '0110'], ['0010100', '1011101', '1111111', '0111110', '0011100', '0001000', '0001000']];
//                  count nMin nMax amp drift land
const LEAF_DEP = [[28, 7, 10, 4, 24, [126, 184]], [16, 10, 13, 7, 40, [196, 228]], [8, 13, 16, 12, 60, [H + 12, H + 12]]];
const MY_LEAVES = LEAF_DEP.map(([count, nMin, nMax], d) => {
  const r = rng(3100 + d), out = [];
  for (let i = 0; i < count; i++) out.push({ id: d * 100 + i, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(), ks: 36 + Math.floor(r() * 30), p2: r(),
    kr: 20 + Math.floor(r() * 25), p3: r(), kf: 26 + Math.floor(r() * 40), p4: r(), amp: 0.7 + r() * 0.6, drift: 0.5 + r(), col: Math.floor(r() * 3) });
  return out;
});
function makeLeafSpr(lc, size, back) {
  const S = LEAF_SH[size], w = S[0].length, h = S.length, mid = w >> 1, p = pix(w, h);
  S.forEach((row, j) => { for (let i = 0; i < w; i++) if (row[i] === '1') {
    let col = back ? lc.back : lc.base;
    if (size === 2 && i === mid && j > 0) col = back ? lc.base : lc.dark;
    else if (!back && size > 0 && i < mid && (i + j) % 3 === 0) col = lc.light;
    else if (size === 2 && (i === 0 || i === w - 1 || j === h - 3)) col = lc.dark;
    p.set(i, j, col);
  } });
  return p.done();
}
function drawLeaves(depth, t) {
  const [, , , amp, drift, band] = LEAF_DEP[depth], S = LEAF_SH[depth], w = S[0].length, h = S.length;
  for (const l of MY_LEAVES[depth]) {
    const { cyc, u } = cycle(t, l.n, l.ph), sw = wave(t, l.ks, l.p2);
    const yEnd = lerp(band[0], band[1], hash(l.id, cyc, 6));
    const y = -10 + u * (yEnd + 10) - Math.abs(sw) * (depth + 1) * 1.5;
    const x = -30 + hash(l.id, cyc, 5) * (W + 24) + u * drift * l.drift + sw * amp * l.amp;
    if (x < -10 || x > W + 10) continue;
    const fl = Math.cos(TAU * (l.kf * t / LOOP + l.p4)), spr = LEAF_SPR[(l.col + cyc) % 3][depth][fl < 0 ? 1 : 0];
    ctx.globalAlpha = band[0] < H && u > 0.9 ? (1 - u) / 0.1 : 1;
    if (depth === 0) { ctx.drawImage(spr, Math.round(x), Math.round(y)); continue; }
    ctx.save();
    ctx.translate(Math.round(x), Math.round(y)); ctx.rotate(sw * 0.9 + 0.3 * wave(t, l.kr, l.p3)); ctx.scale(1, Math.max(0.25, Math.abs(fl)));
    ctx.drawImage(spr, -(w >> 1), -(h >> 1));
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

/* ================= A picked weather (site/js/engine/weather.js) ================= */
// Lisbon draws its own yellow leaves (the city's own weather and the leaves pick); rain and snow land on the river and the
// roofs, on the Largo and the parapet, and past the bottom of the frame, and catch the lamps, the tasca, the cart's fire,
// the floodlit Panteão and São Vicente and the cruise ship's glow
const WEATHER = weatherPlans(['leaves']);
const WX_LAND = [[128, 184], [194, 214], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: LAMP.x, y: LAMP.y + 16, rx: 34, ry: 46 }, { x: TASCA.x + 9, y: TASCA.y + 8, rx: 14, ry: 12 },
  { x: 235, y: 162, rx: 12, ry: 14 }, { x: ST_LAMPS[0].x, y: 162, rx: 14, ry: 16 }, { x: ST_LAMPS[1].x, y: 162, rx: 14, ry: 16 },
  { x: CART.x + 22, y: CART.y - 30, rx: 16, ry: 14, col: '#ffb070' }, { x: PN.cx, y: 88, rx: 32, ry: 46, col: '#f0e8dc' },
  { x: 144, y: 108, rx: 30, ry: 26, col: '#f0d8b4' }, { x: SHIP.x + 32, y: SHIP.y + 12, rx: 36, ry: 14, col: '#ffe8c8' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + 23, y: BB.y + 8, rx: 30, ry: 16, col: '#ffe8c8' }] };

/* ================= Pre-render everything static ================= */
let HL, SKY_C, CLOUD_C, FAR_C, WATER_C, MIST_C, HILL_C, WALL_BB, WALL_BARE, AD, BB_PITCH, SHIP_C, ROOF_C, STREET_C, TRAM_C,
  CORNER_C, TERRACE_C, CART_C, FERRY_C, FERRY_REF, PUFF_C, LEAF_SPR, VIG_C;
function renderVignette() {
  const p = pix(W, H), c = [8, 4, 18];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function prerender() {
  HL = {
    moon: makeHalo(24, '#e8e4d0', 0.16, 1.4), landing: makeHalo(6, '#fffcf0', 0.5, 1), strobe: makeHalo(4, '#e8f0ff', 0.6, 1),
    red: makeHalo(3, '#ff3a3a', 0.5, 1), ferry: makeHalo(8, '#ffe0a8', 0.25), lantern: makeHalo(6, '#ffd890', 0.4),
    flood: makeHalo(40, '#ffe8c8', 0.06, 1.2), floodSV: makeHalo(40, '#ffc890', 0.06, 1.2), bbGlow: makeHalo(30, '#ffe0c0', 0.1), bbLamp: makeHalo(6, '#ffe8c8', 0.5),
    ship: makeHalo(46, '#ffe4b8', 0.1, 1.3), alley: makeHalo(9, '#ffa850', 0.2), wall: makeHalo(18, '#ffc878', 0.22, 1.5),
    shop: makeHalo(14, '#ffc070', 0.2), shopS: makeHalo(9, '#ffa850', 0.2), tram: makeHalo(38, '#ffd890', 0.12, 1.3),
    spark: makeHalo(9, '#b8e4ff', 0.6, 1.2), head: makeHalo(6, '#fff2d0', 0.5), beam: makeHalo(14, '#fff0c8', 0.14, 1.2),
    tail: makeHalo(3, '#ff3a2a', 0.5, 1), tasca: makeHalo(16, '#ffa850', 0.22, 1.4), door: makeHalo(14, '#ff9a48', 0.2),
    ember: makeHalo(10, '#ff7a2a', 0.35, 1.4), roast: makeHalo(12, '#ffb060', 0.2, 1.4),
    lamp: makeHalo(46, '#ffd49a', 0.15, 1.7), lampCore: makeHalo(10, '#fff4dc', 0.45, 1.4),
  };
  SKY_C = renderSky(); STARS = makeStars(); CLOUD_C = renderClouds(); FAR_C = renderFar(); WATER_C = renderWater(); MIST_C = renderMist();
  HILL_C = renderHill(); WALL_BB = renderGableWall(true); WALL_BARE = renderGableWall(false);
  AD = renderAd(); BB_PITCH = makePitch(BB.w, BB.h, { accent: '#3d6bff' });
  SHIP_C = renderShip(); ROOF_C = renderRoofs(); STREET_C = renderStreet(); TRAM_C = { r: renderTram(1), l: renderTram(-1) };
  CORNER_C = renderCorner(); TERRACE_C = renderTerrace(); CART_C = renderCart();
  FERRY_C = sprite(FERRY_ROWS, { m: '#ffffff', w: '#d8d8e0', l: '#ffd890', c: '#8a90a8', o: '#e0782a', d: '#1c1c2a' });
  FERRY_REF = sprite(['..l.l.l.l.l.l...', '...l...l...l....'], { l: '#ffd890' });
  PUFF_C = [0, 1].map(w => Array.from({ length: 12 }, (_, r) => makePuff(Math.max(1, r), w ? '#fff0d8' : '#e4e8f4')));
  const dim = lc => Object.fromEntries(Object.entries(lc).map(([k, v]) => [k, mixHex(v, '#141830', 0.45)]));   // far ones sit in the dark
  LEAF_SPR = LEAF_PAL.map(lc => [0, 1, 2].map(s => [makeLeafSpr(s ? lc : dim(lc), s, false), makeLeafSpr(s ? lc : dim(lc), s, true)]));
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
/** render(t) paints the city's own custard-tart ad; { pitch } paints the site's face over it; { billboard: false } leaves out
 *  the ad, its border and lamps (the gable wall stays, bare); { weather } draws a WEATHERS pick */
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  ctx.fillStyle = '#0a0c18'; ctx.fillRect(0, 0, W, H);
  ctx.drawImage(SKY_C, 0, 0);
  halo(HL.moon, MOON.x, MOON.y, 0.7);
  drawStars(t);
  const off = Math.floor(frac(t / LOOP) * W);
  ctx.drawImage(CLOUD_C, off - W, 8); ctx.drawImage(CLOUD_C, off, 8);
  drawPlane(t);
  drawFar(t);
  blit(WATER_C);
  reflect(ctx, t, { top: RIV.top, rows: 26, x: RIV.x0, w: W - RIV.x0, alpha: 0.4, squash: 1.3 });
  streak(ctx, t, MOON.x, 4, '#fff4d8', 0.8, 3.1, RIV.top + 1, 170);                // the moon's glitter path
  streak(ctx, t, SHIP.x + 30, 8, '#ffe0a8', 0.35, 1.7, SHIP.y + 26, 176);           // the ship's lights on the water
  drawFerry(t);
  const mo = Math.floor(frac(2 * t / LOOP) * W);
  ctx.drawImage(MIST_C, mo - W, HZ - 9); ctx.drawImage(MIST_C, mo, HZ - 9);
  if (wx.leaves) drawLeaves(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  blit(HILL_C); drawHillFx(t);
  blit(billboard ? WALL_BB : WALL_BARE);
  if (billboard) drawBillboard(t, pitch);
  drawShip(t);
  drawGulls(t);
  blit(ROOF_C); drawRoofFx(t); drawRidgeCat(t);
  blit(STREET_C); drawStreetFx(t);
  drawRider(t);
  drawTram(t);
  blit(CORNER_C); drawCornerFx(t);
  if (wx.leaves) drawLeaves(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  ctx.drawImage(TERRACE_C, 0, 0);
  blit(CART_C); drawCartFx(t); drawVendor(t);
  drawBenchMan(t);
  drawCouple(t); drawParapetGull(t);
  drawLamp(t);
  drawSmoke(t);
  if (wx.leaves) drawLeaves(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'lisbon',
  name: 'Lisbon',
  tagline: 'Chestnut smoke over Alfama',
  timeZone: 'Europe/Lisbon',
  accent: '#3d6bff',                                  // azulejo cobalt, written out: tools/seo/seo.mjs reads it from the source
  focusX: PN.cx,
  landmark: [PN.x0, PN.x1],                           // the Panteão Nacional, corner tower to corner tower
  landmarkTop: PN.tip,                                // the row of the finial on the dome's lantern
  billboard: BB,
  ambience: { water: 0.28, crowd: 0.16, traffic: 0.1, wind: 0.08 },
  events: [
    { t: 14, type: 'gull', x: 372, gain: 0.5 },
    { t: 26, type: 'train', dur: 27, pan: [-1, -0.2], gain: 0.4 },
    ...TRAM_BELLS.map((t, i) => ({ t, type: 'chime', x: [10, 90, 210][i], gain: 0.3 })),
    { t: FERRY.t0 + 1, type: 'horn', x: 330, gain: 0.25 },
    { t: FERRY.t0 + 1.5, type: 'boat', dur: 70, pan: [0.4, 1], gain: 0.2 },
    { t: 78, type: 'gull', x: 300, gain: 0.45 },
    { t: SHIP_HORN, type: 'horn', x: 366, gain: 0.5 },
    ...BELLS.map(t => ({ t, type: 'bell', x: 144, gain: 0.4 })),
    { t: 146, type: 'train', dur: 13, pan: [-0.2, -1], gain: 0.45 },
    { t: 190, type: 'gull', x: 372, gain: 0.45 },
    { t: GULL_CALL, type: 'gull', x: 396, gain: 0.55 },
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
